/**
 * =============================================================================
 * DATASHEET COMPONENT - AI-NATIVE DESIGN SYSTEM
 * =============================================================================
 *
 * A standardized spreadsheet-like table component for all data grids.
 *
 * DESIGN PRINCIPLES:
 * - Spreadsheet-like layout (not card-based)
 * - Sheet tabs for contextual switching
 * - Compact toolbar (max 40px height)
 * - Sticky headers
 * - Subtle grid lines (1px, light neutral)
 * - 8pt spacing grid
 * - Semantic colors only
 *
 * STRUCTURE:
 * ┌─────────────────────────────────────────────────────────────┐
 * │ [Tab1] [Tab2] [Tab3]                                        │  ← Sheet Tabs
 * ├─────────────────────────────────────────────────────────────┤
 * │ Title + Summary          │ 🔍 [Filters] [Sort] [Download]  │  ← Header Bar (40px)
 * ├─────────────────────────────────────────────────────────────┤
 * │ Col1  │ Col2  │ Col3  │ Col4  │ Col5  │ Col6  │ Col7  │    │  ← Sticky Header
 * ├───────┼───────┼───────┼───────┼───────┼───────┼───────┼────┤
 * │ Data  │ Data  │ Data  │ Data  │ Data  │ Data  │ Data  │    │  ← Data Rows (44-48px)
 * │ Data  │ Data  │ Data  │ Data  │ Data  │ Data  │ Data  │    │
 * ├───────────────────────────────────────────────────────────────┤
 * │ Legend (optional)                                            │
 * ├───────────────────────────────────────────────────────────────┤
 * │ Footer (pagination, etc.)                                    │
 * └─────────────────────────────────────────────────────────────┘
 */

import { formatCount } from '@utils/currency';
import React, {
    memo,
    lazy,
    Suspense,
    useContext,
    useState,
    useCallback,
    useMemo,
    useEffect,
    useRef,
    type FC,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { toJpeg } from 'html-to-image';
import {
    measureElement as measureVirtualElement,
    useVirtualizer,
} from '@tanstack/react-virtual';
import {
    Badge,
    Box,
    Group,
    Text,
    Skeleton,
    ActionIcon,
    Tooltip,
    TextInput,
    Menu,
    Popover,
} from '@mantine/core';
import {
    MagnifyingGlass,
    CaretUp,
    CaretDown,
    CaretRight,
    DownloadSimple,
    X,
    ShareNetwork,
    ChatCircleDots,
    Rows,
    Funnel,
    Sparkle,
    TreeStructure,
    EnvelopeSimple,
} from '@phosphor-icons/react';
import AppProviderContext from '@providers/App/context';

import { ColumnPicker } from '../ColumnPicker';

/** The hidden-column set a grid remembered under its picker key; empty when there is none or storage is unavailable. */
const readHiddenColumns = (key: string | undefined): string[] => {
    if (!key) return [];
    try {
        const raw = window.localStorage.getItem(key);
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
    } catch {
        return [];
    }
};
type ColumnGroup<T extends Record<string, any>> = { name: string | null; tooltip?: string; columns: DataSheetColumn<T>[] };
/** Consecutive columns sharing a `group` form one header band, in array order. */
function groupDataSheetColumns<T extends Record<string, any>>(columns: DataSheetColumn<T>[]): ColumnGroup<T>[] {
    const groups: ColumnGroup<T>[] = [];
    let currentGroup: string | null = null;
    let currentColumns: DataSheetColumn<T>[] = [];
    const pickTooltip = (cols: DataSheetColumn<T>[]): string | undefined => cols.find((c) => c.groupTooltip)?.groupTooltip;
    columns.forEach((col) => {
        if (col.group !== currentGroup) {
            if (currentColumns.length > 0) groups.push({ name: currentGroup, tooltip: pickTooltip(currentColumns), columns: currentColumns });
            currentGroup = col.group || null;
            currentColumns = [col];
        } else {
            currentColumns.push(col);
        }
    });
    if (currentColumns.length > 0) groups.push({ name: currentGroup, tooltip: pickTooltip(currentColumns), columns: currentColumns });
    return groups;
}
/** Column indices that close a group band (a stronger right border), none when no column is grouped. */
function groupBoundarySet<T extends Record<string, any>>(groups: ColumnGroup<T>[], hasGroupHeaders: boolean): Set<number> {
    const set = new Set<number>();
    if (!hasGroupHeaders) return set;
    let runningIdx = 0;
    groups.forEach((group, gIdx) => {
        if (gIdx < groups.length - 1) set.add(runningIdx + group.columns.length - 1);
        runningIdx += group.columns.length;
    });
    return set;
}
const writeHiddenColumns = (key: string | undefined, keys: string[]): void => {
    if (!key) return;
    try {
        window.localStorage.setItem(key, JSON.stringify(keys));
    } catch {
        // storage unavailable: the choice lasts the session
    }
};

/**
 * The email composer rides on every grid, so it loads only when someone opens it — the
 * composer pulls in the report-schedule API and side panel, which a page listing one table
 * should not pay for up front.
 */
const EmailComposerDrawer = lazy(() => import('../../containers/reportSchedule/EmailComposerDrawer'));
import { generateTableView } from '@api/tableView';
import EntityFilterDropdown, {
    type FilterParam,
    type FilterValue,
} from '../EntityFilterDropdown';
import cellStyles from './DataSheet.module.css';
import { DEFAULT_COLUMN_WIDTH, dataSheetTokens } from './tokens';
import {
    EMPTY_VIEW_SPEC,
    evaluateView,
    getColumnFilter,
    getColumnSort,
    setColumnFilter,
    setColumnSort,
    type CellResolver,
    type CellValue,
    type HighlightRule,
    type ViewOperator,
    type ViewSpec,
} from './viewSpec';

import { currentAppPath, registerCaptureSheet } from '@utils/headlessCapture';
import { EXPLOSION_ORIGIN_KEY } from '@utils/skuExplosion';

// Re-export tokens for backward compatibility
export { dataSheetTokens };
export * from './viewSpec';

// Internal reference
const tokens = dataSheetTokens;

/**
 * Every body cell clips with an ellipsis (DataSheet.module.css); a `wrap: true` column opts
 * back into multi-line text. Row-level classes (getRowClassName) ride along on column 0.
 */
const bodyCellClassName = (col: { wrap?: boolean }, rowClass?: string): string =>
    [cellStyles.cell, col.wrap ? cellStyles.wrap : '', rowClass ?? ''].filter(Boolean).join(' ');

/**
 * The full text as a native tooltip, but only when something in the cell is actually cut:
 * a number that fits gets no tooltip, a product name that does not gets the whole name.
 * Decided on hover rather than at render so it tracks the live column width for free.
 */
const revealClippedCellText = (event: React.MouseEvent<HTMLTableCellElement>): void => {
    const cell = event.currentTarget;
    const clipped = [cell, ...Array.from(cell.querySelectorAll<HTMLElement>('*'))].some(
        (el) => el.scrollWidth > el.clientWidth + 1,
    );
    if (clipped) {
        cell.title = cell.innerText.trim();
    } else {
        cell.removeAttribute('title');
    }
};

/**
 * Floor for a column's width: the widest single word of its header label plus
 * the header furniture (cell padding + sort caret). Header labels wrap at word
 * boundaries only, so a column narrower than its longest word would force that
 * word to overflow the cell and paint over the neighbouring header. The
 * per-character width deliberately over-estimates the 11px/600/uppercase
 * header font so a short estimate can never reintroduce that bleed.
 */
const HEADER_CHAR_WIDTH = 7.6;
const HEADER_FURNITURE_WIDTH =
    2 * tokens.spacing.md + // th horizontal padding
    10 + // sort caret
    tokens.spacing.xxs; // caret gap

const headerMinWidthCache = new Map<string, number>();

function headerMinWidth(header: string): number {
    const cached = headerMinWidthCache.get(header);
    if (cached !== undefined) return cached;
    // Underscores and hyphens count as break opportunities: hyphens genuinely
    // break in CSS, and machine-name headers (zepto_Ahmedabad) are displayed
    // space-separated via renderHeader while `header` keeps the raw name.
    const longestWord = header
        .split(/[\s_-]+/)
        .reduce((max, word) => Math.max(max, word.length), 0);
    const width =
        Math.ceil(longestWord * HEADER_CHAR_WIDTH) + 2 + HEADER_FURNITURE_WIDTH;
    headerMinWidthCache.set(header, width);
    return width;
}

/**
 * The width a column actually renders at. `'auto'` and undefined both fall
 * back to the default; every column is floored at what its header label needs.
 */
function resolveColumnWidth(col: {
    width?: number | 'auto' | 'fit';
    header?: string;
}): number {
    // `'fit'` is measured into a number where the grid first reads its columns (see
    // `fitColumnWidth`); one reaching here unresolved gets the default like `'auto'`.
    const declared =
        typeof col.width === 'number' ? col.width : DEFAULT_COLUMN_WIDTH;
    return typeof col.header === 'string' && col.header
        ? Math.max(declared, headerMinWidth(col.header))
        : declared;
}

/**
 * Cap for a `'fit'` column: a 185-character product name wraps at this width, as it always did,
 * rather than becoming a column nobody can scroll past.
 */
const FIT_MAX_WIDTH = 420;
/** The cell font: `tokens.typography.cell` size, the app's body family (styles/index.css). */
const FIT_FONT = `${tokens.typography.cell.fontSize}px Inter, sans-serif`;
/**
 * Pixels added to a measurement. The webfont can land after the first paint, and a column
 * measured against the fallback face must still hold the value once Inter swaps in.
 */
const FIT_SLACK = 8;

let measureContext: CanvasRenderingContext2D | null | undefined;

/** The text's width in the cell font, or a per-character estimate where canvas is unavailable (tests). */
function textWidth(text: string): number {
    if (measureContext === undefined) {
        measureContext =
            typeof document === 'undefined'
                ? null
                : document.createElement('canvas').getContext('2d');
    }
    if (!measureContext) return text.length * HEADER_CHAR_WIDTH;
    measureContext.font = FIT_FONT;
    return measureContext.measureText(text).width;
}

/** A `'fit'` column's width: its longest value in `data`, padded, floored at the default and capped. */
function fitColumnWidth<T>(col: DataSheetColumn<T>, data: T[]): number {
    let longest = 0;
    for (const row of data) {
        const value = col.valueAccessor ? col.valueAccessor(row) : (row as Record<string, unknown>)[col.key];
        if (value == null) continue;
        longest = Math.max(longest, textWidth(String(value)));
    }
    const padded = Math.ceil(longest) + 2 * tokens.spacing.md + FIT_SLACK;
    return Math.min(FIT_MAX_WIDTH, Math.max(DEFAULT_COLUMN_WIDTH, padded));
}

/**
 * Marks a row that absorbed others when break-down is on, and says which.
 *
 * Rendered by DataSheet rather than by each grid: a number that silently moved is the one
 * thing this feature must never do, so the affordance has to come with the mechanism instead
 * of being remembered separately in every container. Reads the origin the explosion attached
 * to the row, so a grid needs no extra wiring to get it.
 */
const ExplodedBadge = memo<{
    contributors: ReadonlyArray<{ parentId: string; quantity: number }>;
    onClick?: () => void;
}>(({ contributors, onClick }) => {
    if (contributors.length === 0) return null;
    const label = contributors
        .map((c) => (c.quantity === 1 ? c.parentId : `${c.parentId} × ${c.quantity}`))
        .join(', ');
    return (
        <Tooltip
            label={
                onClick
                    ? `Broken down from ${label} — click for the arithmetic`
                    : `Broken down from ${label}`
            }
            multiline
            w={300}
        >
            <Badge
                size="xs"
                variant="light"
                color="blue"
                ml={6}
                {...(onClick
                    ? {
                          component: 'button' as const,
                          type: 'button' as const,
                          onClick: (event: React.MouseEvent) => {
                              // The cell is also the row-click target.
                              event.stopPropagation();
                              onClick();
                          },
                      }
                    : {})}
            >
                +{contributors.length}
            </Badge>
        </Tooltip>
    );
});
ExplodedBadge.displayName = 'ExplodedBadge';

/** Subtle row-background tints for ViewSpec highlight rules (agent / conditional formatting). */
const HIGHLIGHT_BG: Record<HighlightRule['style'], string> = {
    amber: 'rgba(245, 158, 11, 0.12)',
    red: 'rgba(239, 68, 68, 0.10)',
    green: 'rgba(34, 197, 94, 0.12)',
    blue: 'rgba(1, 149, 255, 0.10)',
};

// =============================================================================
// TYPES
// =============================================================================

export interface DataSheetTab {
    id: string;
    label: string;
    icon?: ReactNode;
}

/** One entity filter entry for the DataSheet header bar. */
export interface DataSheetFilterConfig {
    /** Trigger label (e.g. "Product", "Channel"). */
    label: string;
    /** Filter parameters — 1 param → flat; 2+ → nested two-pane. */
    params: FilterParam[];
    /** Current selected values per param key. */
    value: FilterValue;
    /** Called when the user changes the selection. */
    onChange: (value: FilterValue) => void;
}

export type { FilterParam, FilterValue };

export interface DataSheetColumn<T = any> {
    /** Unique column key */
    key: string;
    /** Column header label */
    header: string;
    /**
     * Column width in px, `'auto'` (the 150px default), or `'fit'`: as wide as the longest value
     * in `data`, measured in the grid font, between the default and `FIT_MAX_WIDTH`.
     *
     * `'fit'` is for identity columns — codes and names that are one unbreakable word
     * (Seasoning_MAKHANA_KURKURE). CSS never breaks at an underscore and the fixed-layout cell
     * does not clip, so at a guessed width the part that tells two codes apart is exactly the
     * part that overran the column. Measured, not estimated: four hand-tuned per-character
     * constants preceded this and each was wrong for some catalogue.
     */
    width?: number | 'auto' | 'fit';
    /** Text alignment */
    align?: 'left' | 'center' | 'right';
    /** Is this column sticky (left) */
    sticky?: boolean;
    /** Enable sorting */
    sortable?: boolean;
    /** Custom cell renderer */
    render?: (value: any, row: T, rowIndex: number) => ReactNode;
    /**
     * Let this column's text wrap onto several lines. Off by default: a body cell clips with
     * an ellipsis and shows the full value as a tooltip on hover, so a long name never paints
     * over its neighbour. Reserve this for prose (notes, reasons), never for identity.
     */
    wrap?: boolean;
    /** Custom header renderer */
    renderHeader?: () => ReactNode;
    /** Column group header (for grouped columns) */
    group?: string;
    /**
     * A second, outer header band above `group` — e.g. superGroup "From: Delhi" spanning the
     * groups "Node total", "DEL-1", "DEL-2". Consecutive columns with the same superGroup are
     * spanned together. Requires `group` to be set on the sheet (the band renders with the group band).
     */
    superGroup?: string;
    /** Optional native tooltip applied to the group header cell */
    groupTooltip?: string;
    /**
     * Header for this column's SECOND rendered line in structural exports. On screen an
     * identity cell stacks two lines (product id over description); a spreadsheet has no
     * subtext, so captureViewData exports carrying this split the cell into two columns —
     * line 1 under `header`, the rest under this name.
     */
    captureDetailHeader?: string;
    /**
     * Column section — controls display order independent of array order.
     * sticky   → always first (implicit when sticky=true)
     * entity   → entity-level attributes (category, tier, attrs) — right after sticky
     * metric   → aggregate metrics (inventory totals, DoH) — default if unset
     * grouping → per-period or per-warehouse grouped columns — always last
     */
    section?: 'entity' | 'metric' | 'grouping';
    /**
     * Optional CSV export formatter. When provided, this value is written to the CSV instead of
     * the raw row[key] value. Use this for columns whose render() produces React nodes.
     * Return a string or number; the DataSheet will handle quoting.
     */
    csvValue?: (value: any, row: T) => string | number;
    /**
     * Optional CSV header override. Grouped columns repeat their header across every group
     * (`DRR` under each channel), which is unreadable once the group row is gone — qualify it
     * here (`AMAZON_FBA DRR`). Defaults to `header`.
     */
    csvHeader?: string;
    /** Enable per-column filter popover (Excel-style sort + condition filter). */
    filterable?: boolean;
    /**
     * Returns the value the view engine (sort / filter / highlight / agent) compares on, and the
     * value exposed to the table snapshot. Defaults to `row[key]`. Provide this for columns whose
     * displayed value is derived/computed (i.e. NOT stored at row[key]) so sort, filter, and the
     * AI table agent all operate on what the user actually sees.
     */
    valueAccessor?: (row: T) => string | number | boolean | null;
    /**
     * Exported to CSV but never rendered.
     *
     * For a value the grid shows as subtext inside another cell — a product name under its SKU
     * — which therefore has no column of its own and silently never reached the download. A
     * spreadsheet has no subtext, so it needs a real column; the screen does not want a second
     * one competing with the numbers.
     */
    csvOnly?: boolean;
}

export interface DataSheetProps<T extends Record<string, any> = any> {
    /** Data rows */
    data: T[];
    /** Column definitions */
    columns: DataSheetColumn<T>[];
    /** Sheet tabs (optional) */
    tabs?: DataSheetTab[];
    /** Active tab ID */
    activeTab?: string;
    /** Tab change handler */
    onTabChange?: (tabId: string) => void;
    /** Title (left side of header bar) */
    title?: string | ReactNode;
    /** Subtitle/summary (left side of header bar) */
    subtitle?: string;
    /** Enable search */
    enableSearch?: boolean;
    /** Search placeholder */
    searchPlaceholder?: string;
    /** Search value (controlled) */
    searchValue?: string;
    /** Search change handler */
    onSearchChange?: (value: string) => void;
    /** Enable download button */
    enableDownload?: boolean;
    /**
     * Custom download handler. When provided, this takes priority over all built-in download logic.
     * Prefer onFetchAllData for server-paginated tables instead.
     */
    onDownload?: () => void;
    /**
     * Async function that fetches ALL rows for download (bypasses the current paginated `data` prop).
     * Optionally returns `extraColumns` for tables with dynamic columns (e.g. per-warehouse, per-period).
     * DataSheet merges allColumns + extraColumns and generates the CSV automatically.
     * Use this for server-paginated tables instead of a custom onDownload handler.
     */
    onFetchAllData?: () => Promise<{
        rows: T[];
        extraColumns?: DataSheetColumn<T>[];
    }>;
    /**
     * Full set of columns including those currently hidden via show/hide.
     * When provided, the built-in CSV download includes these additional columns even if they
     * are not rendered in the visible table.
     */
    allColumns?: DataSheetColumn<T>[];
    /** Filename for the built-in CSV download (default: "download.csv") */
    downloadFilename?: string;
    /** Entity filters rendered in the header bar before toolbarControls. */
    filters?: DataSheetFilterConfig[];
    /** Custom toolbar controls (right side) */
    toolbarControls?: ReactNode;
    /** Loading state */
    isLoading?: boolean;
    /** Empty state content */
    emptyState?: ReactNode;
    /** Row click handler */
    onRowClick?: (row: T, rowIndex: number) => void;
    /**
     * Makes rows expandable. Return the child rows for a row, or undefined/[] if it has none.
     *
     * Children are the SAME row type as their parent, so every column renderer works
     * unchanged at both levels — model a rollup row as a row whose numeric fields are the
     * aggregates of its children. Expanded children are flattened into the virtualized
     * list, so a large group costs no more than the rows actually on screen.
     *
     * `getRowKey` must return distinct keys for parents and children.
     */
    getSubRows?: (row: T) => T[] | undefined;
    /** Fired when a row is expanded or collapsed. */
    onRowExpandChange?: (row: T, expanded: boolean) => void;
    /**
     * Adds a "break down to base SKUs" toggle to the identity column's header — the control
     * for restating combo / variant / clearance rows as the SKUs they are made of.
     *
     * DataSheet renders the control and nothing else. It deliberately does NOT do the
     * exploding: which of a row's fields are quantities (which scale and add) and which are
     * ratios (which must be recomputed, never added) is knowledge only the grid has, and
     * getting it wrong turns a 16-day cover figure into a 3,812-day one. Produce this object
     * with `useSkuExplosion`, which also hands back the rows to pass as `data`.
     */
    explosion?: {
        available: boolean;
        enabled: boolean;
        onToggle: (enabled: boolean) => void;
        label: string;
        affectedRowCount: number;
        /** Set when the relation graph failed to load — renders the control disabled. */
        error?: string;
        /**
         * Opens this row's derivation. When provided the `+N` badge becomes a button;
         * otherwise it is informational with the contributors in its tooltip.
         */
        onShowDetail?: (row: T) => void;
    };
    /** Get row key */
    getRowKey?: (row: T, index: number) => string;
    /** Custom row className */
    getRowClassName?: (row: T, index: number) => string;
    /** Max height (for scrollable container). Use 'auto' to fit content, or number for fixed max. */
    maxHeight?: number | string | 'auto';
    /** Min height for the content area */
    minHeight?: number | string;
    /** Override the virtualized body row height (px). Defaults to the design-system row height. */
    rowHeight?: number;
    /** Hide header bar */
    hideHeaderBar?: boolean;
    /** Legend content (appears below table, above footer) */
    legend?: ReactNode;
    /** Footer content (pagination, etc.) */
    footer?: ReactNode;
    /** Custom children (replaces internal table) - use for charts or custom content */
    children?: ReactNode;
    /** Container className */
    className?: string;
    /** Bare mode - renders just the table without outer card wrapper (for nested use) */
    bare?: boolean;
    /** Content type hint - 'table' (default), 'chart', or 'custom' */
    contentType?: 'table' | 'chart' | 'custom';
    /** Share handler — receives the active tab ID (if tabs exist). Renders share icon when provided. */
    onShare?: (activeTabId?: string) => void;
    /** Pre-computed aggregate values per column key, rendered as a sticky summary row below column headers. */
    aggregateRow?: Partial<Record<string, ReactNode>>;
    /**
     * Aggregate computed from the rows the grid is actually showing — search and column
     * filters applied, pagination NOT. Takes precedence over `aggregateRow`.
     *
     * `aggregateRow` is computed by the parent over the data it passed in, so it keeps
     * answering for the whole dataset while the grid filters underneath it: search one SKU
     * and the total still reads for all 170. Paging is deliberately excluded — a page is a
     * viewport, not a selection, so "page 2 of 2" must not change the total.
     *
     * Memoize the function (`useCallback`) — it runs on every filter change.
     */
    aggregateFn?: (rows: T[]) => Partial<Record<string, ReactNode>>;
    /** When provided, the title area becomes clickable — clicking adds this widget to the AI chat. */
    onAddToChat?: () => void;
    /**
     * Page-size options. When provided, enables built-in pagination with a page-size selector.
     * The footer is only rendered when totalPages > 1 (hidden for small datasets).
     * Omit (or leave undefined) to show all rows with no pagination.
     */
    pageSizeOptions?: number[];
    /** Default page size — must be one of pageSizeOptions. Defaults to pageSizeOptions[0]. */
    defaultPageSize?: number;
    /** Group-by options. When provided, renders a Group by button in the toolbar. */
    groupByOptions?: { label: string; value: string }[];
    /** Currently active group-by value (controlled). */
    activeGroupBy?: string | null;
    /** Called when the user selects or clears a group-by option. */
    onGroupByChange?: (value: string | null) => void;
    /** Icon-action controls rendered after Group by and before Download (right-side action cluster). */
    toolbarActionsRight?: ReactNode;
    /** Called whenever the visible (filtered + searched) rows change. Use to keep an external aggregate row in sync. */
    onFilteredRowsChange?: (rows: T[]) => void;
    /**
     * Controlled (server-side) sort. When `onSortChange` is provided, clicking a sortable
     * column header calls it instead of sorting the `data` prop client-side — the parent is
     * expected to re-fetch already-sorted data. `controlledSortKey` / `controlledSortDirection`
     * drive the header sort indicator. Without `onSortChange`, sorting stays client-side.
     */
    controlledSortKey?: string | null;
    controlledSortDirection?: 'asc' | 'desc' | null;
    onSortChange?: (
        key: string | null,
        direction: 'asc' | 'desc' | null,
    ) => void;
    /**
     * Controlled canonical view projection (filter + sort + highlight). When provided, the DataSheet
     * uses this instead of its internal view state, and routes manual Excel sort/filter edits through
     * `onViewSpecChange`. This is the single spec the AI table agent (and "Reset") also write to —
     * manual controls and the agent are peer producers of ONE ViewSpec. Omit for uncontrolled use.
     */
    viewSpec?: ViewSpec;
    onViewSpecChange?: (spec: ViewSpec) => void;
    /**
     * The AI table-view agent — a ✨ control in the header bar that turns a natural-language request
     * ("highlight rows where forecast > stock", "sort by demand desc") into a ViewSpec applied over
     * the loaded rows, with a one-shot "Reset" to the original view. ON by default for every real
     * table (contentType 'table' with columns); pass `enableAiView={false}` to opt a table out.
     */
    enableAiView?: boolean;
    /**
     * Imperative view-capture handle (the email composer's "table image"). When provided,
     * the grid assigns a `DataSheetCaptureHandle` whose `captureViewImage` renders THIS
     * grid's current view — same columns, grouping, expansion, colors, filters — as a
     * static off-screen copy (virtualization off, first N visual rows) and rasterizes it
     * to a PNG data URL. The off-screen copy reads `displayData` (filtered, pre-pagination),
     * so a capture can include more rows than the page on screen.
     */
    captureRef?: React.MutableRefObject<DataSheetCaptureHandle | null>;
    /**
     * The ✉ control: emails this grid as it stands — the rendered cells as an Excel sheet
     * and, if wanted, a picture of the view — through the report email composer. ON by
     * default for every real table; pass `enableEmail={false}` to opt a table out. It sits
     * in the header bar, or in the bottom bar when the header bar is hidden.
     */
    enableEmail?: boolean;
    /** The email's default name and attachment label; falls back to the grid's string title. */
    emailName?: string;
    /**
     * Show / hide columns from the header bar (the shared ColumnPicker). Hidden columns stay
     * in `allColumns` for CSV and the AI view. Columns with an empty header (action columns)
     * are not listed. Off by default: grids that already wire their own picker keep it.
     */
    enableColumnPicker?: boolean;
    /** localStorage key that remembers the hidden set across visits; without it the choice lasts the session. */
    columnPickerKey?: string;
}

/**
 * The expansion state a capture rendered with, in a replayable form: 'ALL' / 'NONE', or
 * the exact row keys that were expanded when the operator had opened only some groups.
 * Stored in scheduled captures so the headless re-render reproduces the same view.
 */
export type DataSheetCaptureExpansion = 'ALL' | 'NONE' | string[];

export interface DataSheetCaptureResult {
    dataUrl: string;
    /** Visual rows actually rendered (group rows and expanded children each count as one). */
    visualRowCount: number;
    /** Visual rows the current view holds in total. */
    totalVisualRows: number;
    /** The expansion state this capture rendered with — see DataSheetCaptureExpansion. */
    expansion: DataSheetCaptureExpansion;
}

/** One rendered cell, exactly as painted: display text plus any non-default colors. */
export interface DataSheetCaptureCell {
    text: string;
    /**
     * The cell's rendered lines when it stacks more than one (identity cells paint
     * "product id" over "description"). `text` joins them with ' · '; exports that can
     * afford real columns (XLSX) split on these instead.
     */
    lines?: string[];
    /** CSS background-color when not transparent/white. */
    bg?: string;
    /** CSS text color when set on the cell. */
    color?: string;
}

/**
 * The grid's own rendered view as data — the structural export source. Because cells are
 * read back from the SAME off-screen render the image capture rasterizes, every value,
 * grouping row, expansion, hidden column and color matches the screen by construction;
 * there is no server-side mirror to drift.
 */
export interface DataSheetCaptureData {
    columns: Array<{ header: string; group?: string; detailHeader?: string }>;
    /** The grid's aggregate (Total) row, when the view has one. */
    aggregateRow?: DataSheetCaptureCell[];
    rows: DataSheetCaptureCell[][];
    visualRowCount: number;
    totalVisualRows: number;
    expansion: DataSheetCaptureExpansion;
}

interface CaptureOptions {
    maxVisualRows?: number;
    /**
     * Overrides the interactive expansion state ('ALL', 'NONE', or specific row
     * keys). Omit to capture the expansion exactly as the operator left it. Used by
     * the headless email-render route, where nobody has clicked anything.
     */
    expansion?: DataSheetCaptureExpansion;
}

export interface DataSheetCaptureHandle {
    captureViewImage(options?: CaptureOptions): Promise<DataSheetCaptureResult>;
    /** Same off-screen render as the image, serialized instead of rasterized. */
    captureViewData(options?: CaptureOptions): Promise<DataSheetCaptureData>;
}

/** Resolve the value the engine/snapshot compares on for a column: valueAccessor, else row[key]. */
function resolveColumnValue<T extends Record<string, any>>(
    col: DataSheetColumn<T>,
    row: T,
): CellValue {
    if (col.valueAccessor) return col.valueAccessor(row);
    return (row[col.key] ?? null) as CellValue;
}

/**
 * Resolved table snapshot — the context handed to the AI table-view agent: the columns (with their
 * value type) and every row's DISPLAYED value per column, keyed by a stable row key. The agent reads
 * this to translate a prompt into a ViewSpec (it never recomputes data). Mirrors exactly what the
 * view engine sees, so an agent-authored expression evaluates identically to what the user sees.
 */
export interface TableSnapshot {
    primaryKeyField: string;
    columns: {
        key: string;
        header: string;
        type: 'number' | 'string' | 'boolean';
    }[];
    rows: Record<string, CellValue>[];
}

export function buildTableSnapshot<T extends Record<string, any>>(
    rows: T[],
    columns: DataSheetColumn<T>[],
    getRowKey?: (row: T, index: number) => string,
): TableSnapshot {
    const cols = columns.filter((c) => c.key !== '_idx');
    const inferType = (key: string): 'number' | 'string' | 'boolean' => {
        for (const row of rows) {
            const v = resolveColumnValue(
                columns.find((c) => c.key === key)!,
                row,
            );
            if (v === null || v === undefined) continue;
            return typeof v === 'number'
                ? 'number'
                : typeof v === 'boolean'
                  ? 'boolean'
                  : 'string';
        }
        return 'string';
    };
    return {
        primaryKeyField: '__key',
        columns: cols.map((c) => ({
            key: c.key,
            header: c.header,
            type: inferType(c.key),
        })),
        rows: rows.map((row, i) => {
            const out: Record<string, CellValue> = {
                __key: getRowKey?.(row, i) ?? String(i),
            };
            for (const c of cols) out[c.key] = resolveColumnValue(c, row);
            return out;
        }),
    };
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

/** Sheet Tab Button */
const SheetTab = memo<{
    tab: DataSheetTab;
    isActive: boolean;
    onClick: () => void;
}>(({ tab, isActive, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacing.xxs,
            padding: `${tokens.spacing.xs}px ${tokens.spacing.md}px`,
            height: tokens.dimensions.tabHeight,
            fontSize: 12,
            fontWeight: isActive ? 600 : 500,
            color: isActive
                ? tokens.colors.textPrimary
                : tokens.colors.textSecondary,
            backgroundColor: isActive ? tokens.colors.surface : 'transparent',
            border: 'none',
            borderBottom: isActive
                ? `2px solid ${tokens.colors.activeTab}`
                : '2px solid transparent',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
        }}
    >
        {tab.icon}
        {tab.label}
    </button>
));
SheetTab.displayName = 'SheetTab';

/**
 * The sheet-tab strip on its own.
 *
 * Exported because a tabbed screen is not always a grid. The CM site page has five tabs, four of
 * which render a DataSheet and one of which renders a form — and while the strip lived only
 * inside this component, that fifth tab had to hand-roll its own out of buttons, so it looked
 * like a different control on the same screen. Rendering this above the content instead means
 * the strip is the same component whatever sits beneath it.
 */
export const SheetTabStrip: FC<{
    tabs: DataSheetTab[];
    activeTab?: string;
    onTabChange?: (tabId: string) => void;
}> = ({ tabs, activeTab, onTabChange }) => (
    <Box
        style={{
            display: 'flex',
            alignItems: 'flex-end',
            backgroundColor: tokens.colors.headerBg,
            borderBottom: `1px solid ${tokens.colors.border}`,
            paddingLeft: tokens.spacing.xs,
        }}
    >
        {tabs.map((tab) => (
            <SheetTab
                key={tab.id}
                tab={tab}
                isActive={activeTab === tab.id}
                onClick={() => onTabChange?.(tab.id)}
            />
        ))}
    </Box>
);

/** Sort Indicator */
const SortIndicator = memo<{ direction: 'asc' | 'desc' | null }>(
    ({ direction }) => {
        if (!direction) {
            return (
                <Box
                    style={{
                        opacity: 0.3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: -2,
                    }}
                >
                    <CaretUp size={10} weight="bold" />
                    <CaretDown
                        size={10}
                        weight="bold"
                        style={{ marginTop: -4 }}
                    />
                </Box>
            );
        }
        return direction === 'asc' ? (
            <CaretUp
                size={12}
                weight="bold"
                style={{ color: tokens.colors.activeTab }}
            />
        ) : (
            <CaretDown
                size={12}
                weight="bold"
                style={{ color: tokens.colors.activeTab }}
            />
        );
    },
);
SortIndicator.displayName = 'SortIndicator';

/** Loading Skeleton Row */
const SkeletonRow = memo<{ columns: number }>(({ columns }) => (
    <tr>
        {Array.from({ length: columns }).map((_, i) => (
            <td
                key={i}
                style={{
                    padding: `${tokens.spacing.sm}px ${tokens.spacing.md}px`,
                }}
            >
                <Skeleton height={16} radius="sm" />
            </td>
        ))}
    </tr>
));
SkeletonRow.displayName = 'SkeletonRow';

// =============================================================================
// COLUMN FILTER
// =============================================================================

type FilterOperator =
    | 'eq'
    | 'neq'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'contains'
    | 'notContains'
    | 'isEmpty'
    | 'isNotEmpty';

interface ColumnFilter {
    operator: FilterOperator;
    value: string;
}

const FILTER_OPERATORS: { value: FilterOperator; label: string }[] = [
    { value: 'contains', label: 'Contains' },
    { value: 'notContains', label: 'Does not contain' },
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Does not equal' },
    { value: 'gt', label: 'Greater than' },
    { value: 'gte', label: 'Greater than or equal' },
    { value: 'lt', label: 'Less than' },
    { value: 'lte', label: 'Less than or equal' },
    { value: 'isEmpty', label: 'Is empty' },
    { value: 'isNotEmpty', label: 'Is not empty' },
];

const ColumnFilterPopover = memo<{
    isSortedAsc: boolean;
    isSortedDesc: boolean;
    filter: ColumnFilter | undefined;
    onSort: (direction: 'asc' | 'desc') => void;
    onFilter: (filter: ColumnFilter | null) => void;
    onClose: () => void;
}>(({ isSortedAsc, isSortedDesc, filter, onSort, onFilter, onClose }) => {
    const [operator, setOperator] = useState<FilterOperator>(
        filter?.operator ?? 'contains',
    );
    const [value, setValue] = useState(filter?.value ?? '');
    const needsValue = !['isEmpty', 'isNotEmpty'].includes(operator);

    /*
     * Sync the draft to the APPLIED filter's content, never its identity. The parent
     * rebuilds the filter object every render (hover tracking alone re-renders it
     * constantly), so an identity dependency reset the draft mid-edit - pick "Contains"
     * over an applied "Equals", move the mouse, and the select snapped back to Equals.
     */
    useEffect(() => {
        setOperator(filter?.operator ?? 'contains');
        setValue(filter?.value ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter?.operator, filter?.value]);

    const apply = () => {
        if (needsValue && !value.trim()) {
            onFilter(null);
            onClose();
            return;
        }
        onFilter({ operator, value: needsValue ? value.trim() : '' });
        onClose();
    };

    const clear = () => {
        setOperator('contains');
        setValue('');
        onFilter(null);
        onClose();
    };

    const btnBase: React.CSSProperties = {
        flex: 1,
        padding: '5px 8px',
        fontSize: 11,
        border: '1px solid #e0e0e0',
        borderRadius: 5,
        cursor: 'pointer',
        backgroundColor: '#fff',
        color: '#555',
        fontWeight: 400,
    };
    const btnActiveStyle: React.CSSProperties = {
        backgroundColor: 'rgba(1,149,255,0.1)',
        borderColor: 'rgb(1,149,255)',
        color: 'rgb(1,149,255)',
        fontWeight: 600,
    };

    return (
        <div style={{ padding: 14, minWidth: 220, fontSize: 12 }}>
            <div
                style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: '#aaa',
                    marginBottom: 7,
                }}
            >
                Sort
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                <button
                    type="button"
                    onClick={() => {
                        onSort('asc');
                        onClose();
                    }}
                    style={{
                        ...btnBase,
                        ...(isSortedAsc ? btnActiveStyle : {}),
                    }}
                >
                    ↑ A → Z
                </button>
                <button
                    type="button"
                    onClick={() => {
                        onSort('desc');
                        onClose();
                    }}
                    style={{
                        ...btnBase,
                        ...(isSortedDesc ? btnActiveStyle : {}),
                    }}
                >
                    ↓ Z → A
                </button>
            </div>
            <div style={{ borderTop: '1px solid #f0f0f0', marginBottom: 12 }} />
            <div
                style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: '#aaa',
                    marginBottom: 7,
                }}
            >
                Filter by condition
            </div>
            <select
                value={operator}
                onChange={(e) => setOperator(e.target.value as FilterOperator)}
                style={{
                    width: '100%',
                    padding: '5px 8px',
                    fontSize: 11,
                    border: '1px solid #e0e0e0',
                    borderRadius: 5,
                    marginBottom: 6,
                    backgroundColor: '#fff',
                    color: '#333',
                    boxSizing: 'border-box',
                    colorScheme: 'light',
                }}
            >
                {FILTER_OPERATORS.map((op) => (
                    <option key={op.value} value={op.value}>
                        {op.label}
                    </option>
                ))}
            </select>
            {needsValue && (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') apply();
                    }}
                    placeholder="Value..."
                    autoFocus
                    style={{
                        width: '100%',
                        padding: '5px 8px',
                        fontSize: 11,
                        border: '1px solid #e0e0e0',
                        borderRadius: 5,
                        marginBottom: 10,
                        boxSizing: 'border-box',
                        outline: 'none',
                    }}
                />
            )}
            {!needsValue && <div style={{ marginBottom: 10 }} />}
            <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" onClick={clear} style={btnBase}>
                    Clear
                </button>
                <button
                    type="button"
                    onClick={apply}
                    style={{
                        ...btnBase,
                        flex: 1.5,
                        backgroundColor: 'rgb(1,149,255)',
                        border: 'none',
                        color: '#fff',
                        fontWeight: 600,
                    }}
                >
                    Apply
                </button>
            </div>
        </div>
    );
});
ColumnFilterPopover.displayName = 'ColumnFilterPopover';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

function DataSheetInner<T extends Record<string, any>>(
    props: DataSheetProps<T>,
    ref: React.ForwardedRef<HTMLDivElement>,
) {
    const {
        data,
        columns: columnsInput,
        tabs,
        activeTab,
        onTabChange,
        title,
        subtitle,
        enableSearch = false,
        searchPlaceholder = 'Search...',
        searchValue,
        onSearchChange,
        enableDownload = false,
        onDownload,
        onFetchAllData,
        allColumns: allColumnsInput,
        downloadFilename = 'download.csv',
        filters,
        toolbarControls,
        isLoading = false,
        emptyState,
        onRowClick,
        getSubRows,
        onRowExpandChange,
        explosion,
        getRowKey,
        getRowClassName,
        maxHeight,
        minHeight,
        rowHeight,
        hideHeaderBar = false,
        legend,
        footer,
        children,
        className,
        bare = false,
        contentType = 'table',
        onShare,
        aggregateRow,
        aggregateFn,
        onAddToChat,
        pageSizeOptions,
        defaultPageSize,
        groupByOptions,
        activeGroupBy,
        onGroupByChange,
        toolbarActionsRight,
        onFilteredRowsChange,
        controlledSortKey,
        controlledSortDirection,
        onSortChange,
        viewSpec: controlledViewSpec,
        onViewSpecChange,
        enableAiView = true,
        captureRef,
        enableEmail = true,
        emailName,
        enableColumnPicker = false,
        columnPickerKey,
    } = props;
    // Column picker: the hidden set lives here, remembered under `columnPickerKey` when given.
    const [hiddenColumnKeys, setHiddenColumnKeys] = useState<string[]>(() => readHiddenColumns(columnPickerKey));
    useEffect(() => {
        writeHiddenColumns(columnPickerKey, hiddenColumnKeys);
    }, [columnPickerKey, hiddenColumnKeys]);
    const columnsProp = useMemo(() => {
        if (!enableColumnPicker || hiddenColumnKeys.length === 0) return columnsInput;
        const hidden = new Set(hiddenColumnKeys);
        const shown = columnsInput.filter((c) => !hidden.has(c.key));
        return shown.length > 0 ? shown : columnsInput;
    }, [columnsInput, enableColumnPicker, hiddenColumnKeys]);
    const allColumnsProp = allColumnsInput ?? (enableColumnPicker ? columnsInput : undefined);
    const columnPickerDefs = useMemo(
        () => (enableColumnPicker ? columnsInput.filter((c) => c.header !== '').map((c) => ({ key: c.key, label: typeof c.header === 'string' ? c.header : (c.csvHeader ?? c.key) })) : []),
        [columnsInput, enableColumnPicker],
    );

    const activePaginationOptions = pageSizeOptions?.length
        ? pageSizeOptions
        : !bare && !footer
          ? [20, 50, 100]
          : undefined;

    // Selection state (for Add to Chat affordance)
    const [isSelected, setIsSelected] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Deselect when user clicks outside the DataSheet
    useEffect(() => {
        if (!isSelected || !onAddToChat) return;
        const handleOutsideClick = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setIsSelected(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () =>
            document.removeEventListener('mousedown', handleOutsideClick);
    }, [isSelected, onAddToChat]);

    // Local search state if not controlled
    const [localSearch, setLocalSearch] = useState('');
    const search = searchValue ?? localSearch;
    const handleSearchChange = onSearchChange ?? setLocalSearch;

    // ── Canonical view projection (filter + sort + highlight) ─────────────────
    // ONE ViewSpec is the single source of truth for client-side projection. The manual Excel
    // column controls and (Phase 2) the AI table agent are peer producers that both write here.
    // Controlled when `viewSpec` prop is supplied (agent / Reset live in the parent), else internal.
    const [internalViewSpec, setInternalViewSpec] =
        useState<ViewSpec>(EMPTY_VIEW_SPEC);
    const viewSpec = controlledViewSpec ?? internalViewSpec;
    const setViewSpec = useCallback(
        (next: ViewSpec) => {
            if (onViewSpecChange) onViewSpecChange(next);
            if (controlledViewSpec === undefined) setInternalViewSpec(next);
        },
        [onViewSpecChange, controlledViewSpec],
    );

    // ── AI table-view agent (opt-in via enableAiView) ─────────────────────────
    // The agent is just another ViewSpec producer: it reads the resolved table snapshot, returns a
    // spec, and we apply it through the SAME setViewSpec the manual controls use. Reset → as-is view.
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const [aiSummary, setAiSummary] = useState<string | null>(null);
    const [aiError, setAiError] = useState<string | null>(null);
    const [aiOpen, setAiOpen] = useState(false);

    const runAiView = useCallback(async () => {
        const prompt = aiPrompt.trim();
        if (!prompt) return;
        setAiLoading(true);
        setAiError(null);
        try {
            const snapshot = buildTableSnapshot(
                data,
                allColumnsProp ?? columnsProp,
                getRowKey,
            );
            const result = await generateTableView({
                prompt,
                snapshot,
                currentSpec: viewSpec,
            });
            const next: ViewSpec = {
                filters: result.filters,
                sorts: result.sorts,
                highlights: result.highlights,
            };
            if (
                next.filters.length === 0 &&
                next.sorts.length === 0 &&
                next.highlights.length === 0
            ) {
                setAiError(
                    "Couldn't map that to this table. Try naming a column.",
                );
                return;
            }
            setViewSpec(next);
            setAiSummary(result.summary || prompt);
            setAiOpen(false);
            setAiPrompt('');
        } catch {
            setAiError('Something went wrong. Try again.');
        } finally {
            setAiLoading(false);
        }
    }, [
        aiPrompt,
        data,
        allColumnsProp,
        columnsProp,
        getRowKey,
        viewSpec,
        setViewSpec,
    ]);

    const resetAiView = useCallback(() => {
        setViewSpec(EMPTY_VIEW_SPEC);
        setAiSummary(null);
        setAiError(null);
    }, [setViewSpec]);

    // Server-side sort stays orthogonal: when `onSortChange` is provided the parent pre-sorts the
    // `data` prop, so the engine skips sorting and the header indicator reads the controlled props.
    const isControlledSort = !!onSortChange;
    const manualSortKey = (() => {
        const c = viewSpec.sorts.find((s) => 'col' in s.by);
        return c && 'col' in c.by ? c.by.col : null;
    })();
    const manualSortDirection = manualSortKey
        ? getColumnSort(viewSpec, manualSortKey)
        : null;
    const sortKey = isControlledSort
        ? (controlledSortKey ?? null)
        : manualSortKey;
    const sortDirection = isControlledSort
        ? (controlledSortDirection ?? null)
        : manualSortDirection;

    // Per-column value resolver — uses valueAccessor when set, else raw row[key]. Shared by the
    // engine and the table snapshot so sort/filter/agent all act on the DISPLAYED value.
    // Kept identity-STABLE (reads live columns / getRowKey via refs) so the engine memo below only
    // recomputes on real input changes — consumers often pass inline columns/getRowKey, and an
    // unstable resolver + an active filter would otherwise loop with onFilteredRowsChange.
    const colsRef = useRef(allColumnsProp ?? columnsProp);
    colsRef.current = allColumnsProp ?? columnsProp;
    const getRowKeyRef = useRef(getRowKey);
    getRowKeyRef.current = getRowKey;
    /**
     * Read through refs by the CSV builder so it can be declared above the values it needs
     * without dragging them into its dependency array — the alternative was reordering a
     * long component, and a stale closure here would silently export the previous view.
     */
    const getSubRowsRef = useRef(getSubRows);
    getSubRowsRef.current = getSubRows;
    const projectedDataRef = useRef<T[]>([]);
    const resolveCellValue = useCallback<CellResolver<T>>((row, colKey) => {
        const col = colsRef.current.find((c) => c.key === colKey);
        if (col?.valueAccessor) return col.valueAccessor(row);
        const raw = (row as Record<string, unknown>)[colKey];
        return (raw ?? null) as CellValue;
    }, []);

    // Handle sort click — cycles asc → desc → none. Writes to the canonical ViewSpec (or the
    // server-side sort callback when controlled).
    const handleSort = useCallback(
        (key: string) => {
            const currentDir = sortKey === key ? sortDirection : null;
            let nextDir: 'asc' | 'desc' | null = 'asc';
            if (currentDir === 'asc') nextDir = 'desc';
            else if (currentDir === 'desc') nextDir = null;
            if (isControlledSort) onSortChange!(nextDir ? key : null, nextDir);
            else setViewSpec(setColumnSort(viewSpec, key, nextDir));
        },
        [
            sortKey,
            sortDirection,
            isControlledSort,
            onSortChange,
            viewSpec,
            setViewSpec,
        ],
    );

    const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
    const [hoveredHeaderKey, setHoveredHeaderKey] = useState<string | null>(
        null,
    );

    const handleFilter = useCallback(
        (key: string, filter: ColumnFilter | null) => {
            setViewSpec(
                setColumnFilter(
                    viewSpec,
                    key,
                    filter
                        ? {
                              op: filter.operator as ViewOperator,
                              value: filter.value,
                          }
                        : null,
                ),
            );
        },
        [viewSpec, setViewSpec],
    );

    const handleSortDirect = useCallback(
        (key: string, dir: 'asc' | 'desc') => {
            if (isControlledSort) onSortChange!(key, dir);
            else setViewSpec(setColumnSort(viewSpec, key, dir));
        },
        [isControlledSort, onSortChange, viewSpec, setViewSpec],
    );

    // Header reads per-column filter state from the canonical spec (back-compat shape for the popover).
    const columnFilters = useMemo<Record<string, ColumnFilter>>(() => {
        const out: Record<string, ColumnFilter> = {};
        for (const col of columnsProp) {
            const f = getColumnFilter(viewSpec, col.key);
            if (f)
                out[col.key] = {
                    operator: f.op as FilterOperator,
                    value: f.value,
                };
        }
        return out;
    }, [viewSpec, columnsProp]);

    // Download loading state
    const [isDownloading, setIsDownloading] = useState(false);

    /**
     * The row a CSV line is for, plus where it sat in the grid's tree.
     *
     * A grouped grid is a tree on screen and the export has to say so, or the file is a list
     * of group totals with the SKUs behind them missing — which is what it used to be.
     */
    type CsvLine = { row: T; depth: number; isGroup: boolean; path: string[] };

    /** The text the identity column shows for a row — the label a group is known by. */
    const csvIdentityLabel = useCallback(
        (row: T): string => {
            // The identity column: the first sticky one, else the first. Same rule as
            // `explosionAnchorKey`, computed independently here because that const is
            // declared further down and this callback runs above it.
            const col = columnsProp.find((c) => c.sticky) ?? columnsProp[0];
            if (!col) return '';
            const raw = row[col.key];
            const value = col.csvValue
                ? col.csvValue(raw, row)
                : (col.valueAccessor?.(row) ?? raw);
            return value === null || value === undefined ? '' : String(value);
        },
        [columnsProp],
    );

    /**
     * Depth-first flatten of the rows as displayed: each group immediately followed by what it
     * contains, to any depth. Order is the grid's own, so the file reads top-to-bottom exactly
     * like the screen.
     */
    const flattenForCsv = useCallback(
        (rows: T[]): CsvLine[] => {
            const out: CsvLine[] = [];
            const walk = (list: T[], depth: number, path: string[]) => {
                for (const row of list) {
                    // Not named `children` — that shadows the component's own prop.
                    const nested = getSubRowsRef.current?.(row);
                    const isGroup = !!nested && nested.length > 0;
                    out.push({ row, depth, isGroup, path });
                    if (isGroup) {
                        walk(nested, depth + 1, [...path, csvIdentityLabel(row)]);
                    }
                }
            };
            walk(rows, 0, []);
            return out;
        },
        [csvIdentityLabel],
    );

    // Shared CSV builder — works on any rows + column set
    const buildAndTriggerCsv = useCallback(
        (exportCols: DataSheetColumn<T>[], rows: T[], filename: string) => {
            const csvRows: string[] = [];
            const lines = flattenForCsv(rows);
            const maxDepth = lines.reduce((max, line) => Math.max(max, line.depth), 0);

            /**
             * Hierarchy columns, present only when the view actually has a hierarchy — a flat
             * grid exports exactly the columns it shows, with nothing bolted on.
             *
             * They exist because the file has to be usable two ways at once. Read top-to-bottom
             * it mirrors the screen, group totals included. Filtered to `Row type = Item` in
             * Excel it becomes a clean leaf-level table whose columns sum correctly — which
             * matters because a column containing both a group total and its members
             * double-counts if you sum it blind. Percentage columns have the same property:
             * they are shares of a parent, so they only add to 100% within one level.
             */
            const hierarchyHeaders =
                maxDepth > 0
                    ? [
                          'Row type',
                          ...Array.from({ length: maxDepth }, (_, index) =>
                              index === 0 ? 'Group' : `Sub-group ${index}`,
                          ),
                      ]
                    : [];

            const quote = (value: string) => `"${value.replace(/"/g, '""')}"`;

            csvRows.push(
                [
                    ...hierarchyHeaders.map(quote),
                    ...exportCols.map((col) => quote(col.csvHeader ?? col.header)),
                ].join(','),
            );

            for (const line of lines) {
                const hierarchyCells =
                    maxDepth > 0
                        ? [
                              quote(
                                  line.isGroup
                                      ? line.depth === 0
                                          ? 'Group'
                                          : `Sub-group ${line.depth}`
                                      : 'Item',
                              ),
                              // The group path this row sits under, one column per level. A
                              // group row names ITSELF at its own level rather than leaving the
                              // cell blank, so `Group` is populated on every line and Excel can
                              // pivot or subtotal on it without the reader first having to
                              // reconstruct which group a total belonged to.
                              ...Array.from({ length: maxDepth }, (_, index) =>
                                  quote(
                                      line.isGroup && index === line.depth
                                          ? csvIdentityLabel(line.row)
                                          : (line.path[index] ?? ''),
                                  ),
                              ),
                          ]
                        : [];

                const cells = exportCols.map((col) => {
                    const raw = line.row[col.key];
                    if (col.csvValue) {
                        const v = col.csvValue(raw, line.row);
                        return typeof v === 'number' ? v : quote(String(v));
                    }
                    if (raw === null || raw === undefined) return '""';
                    if (typeof raw === 'number') return raw;
                    return quote(String(raw));
                });
                csvRows.push([...hierarchyCells, ...cells].join(','));
            }

            const csv = csvRows.join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);
        },
        [flattenForCsv, csvIdentityLabel],
    );

    // Built-in download handler — priority: onDownload > onFetchAllData > built-in from data
    const handleBuiltinDownload = useCallback(async () => {
        if (onDownload) {
            onDownload();
            return;
        }

        setIsDownloading(true);
        try {
            if (onFetchAllData) {
                // Server-paginated grid: the rows on screen are one page, so the parent
                // re-fetches the full set. `allColumns` is honoured here because such a grid
                // builds its export column list itself.
                const { rows, extraColumns = [] } = await onFetchAllData();
                const exportCols = [
                    ...(allColumnsProp ?? columnsProp),
                    ...extraColumns,
                ];
                buildAndTriggerCsv(exportCols, rows, downloadFilename);
            } else {
                /*
                 * Everything is already here, so the export is the VIEW: the visible columns,
                 * and the rows after this grid's own search / column filters / sort have run.
                 *
                 * `projectedData` rather than `data` on purpose. `data` is what the parent
                 * handed over, before the header-bar filters the user then applied — exporting
                 * it produced a file that disagreed with the screen, which is the one thing a
                 * download must never do. Pagination is deliberately NOT applied: a page is a
                 * viewport, not a selection.
                 */
                buildAndTriggerCsv(
                    // A column with no header cannot be labelled in a CSV — those are icon /
                    // action affordances (the create-PO button), not readable values.
                    columnsProp.filter((col) => (col.csvHeader ?? col.header) !== ''),
                    projectedDataRef.current,
                    downloadFilename,
                );
            }
        } finally {
            setIsDownloading(false);
        }
    }, [
        onDownload,
        onFetchAllData,
        allColumnsProp,
        columnsProp,
        downloadFilename,
        buildAndTriggerCsv,
    ]);

    // Single generic engine pass: filter + sort + highlight from the canonical ViewSpec. Sorting is
    // skipped under controlled (server-side) sort. Manual Excel controls and the agent share this.
    const rowKeyFn = useCallback(
        (row: T, index: number) =>
            getRowKeyRef.current?.(row, index) ?? String(index),
        [],
    );
    const { rows: projectedData, highlightByKey } = useMemo(
        () =>
            evaluateView(data, resolveCellValue, viewSpec, rowKeyFn, {
                skipSort: isControlledSort,
            }),
        [data, resolveCellValue, viewSpec, rowKeyFn, isControlledSort],
    );
    // What the CSV exports. Assigned during render so a download always reflects the paint
    // the user is looking at.
    projectedDataRef.current = projectedData;

    // Internal search filter — only when not externally controlled
    const displayData = useMemo(() => {
        if (!search.trim() || onSearchChange) return projectedData;
        const lower = search.toLowerCase();
        return projectedData.filter((row) =>
            Object.values(row as Record<string, unknown>).some(
                (v) => v != null && String(v).toLowerCase().includes(lower),
            ),
        );
    }, [projectedData, search, onSearchChange]);

    useEffect(() => {
        onFilteredRowsChange?.(displayData);
    }, [displayData, onFilteredRowsChange]);

    // Pagination state — always present; activePaginationOptions=undefined disables slicing
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(
        () => defaultPageSize ?? activePaginationOptions?.[0] ?? 20,
    );

    // Reset to page 1 whenever the filtered dataset changes
    useEffect(() => {
        setPage(1);
    }, [displayData.length, search]);

    const totalPages = activePaginationOptions
        ? Math.max(1, Math.ceil(displayData.length / pageSize))
        : 1;

    const pagedData = useMemo(() => {
        if (!activePaginationOptions) return displayData;
        const start = (page - 1) * pageSize;
        return displayData.slice(start, start + pageSize);
    }, [displayData, page, pageSize, activePaginationOptions]);

    // Expand/collapse state for expandable rows, keyed by row key. Stale keys from a
    // previous dataset are harmless — they simply never match a rendered row.
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
        () => new Set(),
    );

    /**
     * Parents interleaved with the children of expanded ones, so the virtualizer sees a
     * single flat list and an expanded group costs only the rows actually on screen.
     * Filtering, sorting and pagination all ran on parents above — children follow their
     * parent rather than being ranked against it.
     */
    const flatRows = useMemo(() => {
        const flattened: Array<{
            row: T;
            key: string;
            depth: number;
            hasSubRows: boolean;
        }> = [];
        // Recursive, so a grid can nest more than one level — e.g. category → product
        // group → SKU. Previously this walked exactly two levels and stamped
        // `hasSubRows: false` on every child, which meant a grandchild could never be
        // expanded even when the caller supplied one.
        //
        // Additive by construction: a caller whose sub-rows have no sub-rows of their own
        // produces byte-identical output to the old two-level walk.
        const walk = (
            rows: T[],
            depth: number,
            parentKey: string,
        ): void => {
            rows.forEach((row, index) => {
                // ALWAYS namespaced by the parent. A caller's getRowKey is only unique
                // among siblings — a group row's key is typically its label, so an "Others"
                // bucket under two different parents produced the same key, and expanding
                // one expanded every one of them while React rendered duplicates.
                const ownKey = getRowKey ? getRowKey(row, index) : String(index);
                const key = parentKey ? `${parentKey}__${ownKey}` : ownKey;
                const subRows = getSubRows?.(row);
                const hasSubRows = !!subRows && subRows.length > 0;
                flattened.push({ row, key, depth, hasSubRows });
                if (hasSubRows && expandedKeys.has(key)) {
                    walk(subRows, depth + 1, key);
                }
            });
        };
        walk(pagedData, 0, '');
        return flattened;
    }, [pagedData, getSubRows, getRowKey, expandedKeys]);

    /**
     * The Excel-style sort + filter affordance for a column header. Shared by the bare
     * and full header paths — bare mode previously rendered only the sort caret, so
     * every embedded grid in the app (all of Inventory, which renders bare) silently
     * lost per-column filtering.
     */
    const renderColumnFilter = useCallback(
        (col: DataSheetColumn<T>): ReactNode => {
            if (!col.filterable) return null;
            const isActive = !!columnFilters[col.key];
            return (
                <Popover
                    opened={openFilterKey === col.key}
                    onClose={() => setOpenFilterKey(null)}
                    position="bottom-start"
                    withinPortal
                    shadow="md"
                    radius="md"
                >
                    <Popover.Target>
                        <button
                            type="button"
                            aria-label={`Sort or filter ${col.header}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                setOpenFilterKey((prev) =>
                                    prev === col.key ? null : col.key,
                                );
                            }}
                            style={{
                                // Overlaid on the cell's padding zone (opposite
                                // the label's aligned edge) instead of sitting
                                // in the flex flow — an in-flow button reserves
                                // its width even while opacity-0, stealing
                                // label space in narrow columns.
                                position: 'absolute',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                ...(col.align === 'right'
                                    ? { left: 2 }
                                    : { right: 2 }),
                                zIndex: 1,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 16,
                                height: 16,
                                padding: 0,
                                border: 'none',
                                borderRadius: 3,
                                cursor: 'pointer',
                                opacity:
                                    isActive ||
                                    hoveredHeaderKey === col.key ||
                                    openFilterKey === col.key
                                        ? 1
                                        : 0,
                                transition: 'opacity 0.1s ease',
                                // Solid fills (not translucent) — the button
                                // paints over header text in narrow columns.
                                backgroundColor: isActive
                                    ? '#E3F0FD'
                                    : '#EBEBEB',
                                color: isActive
                                    ? 'rgb(1,149,255)'
                                    : tokens.colors.textSecondary,
                            }}
                        >
                            <Funnel
                                size={10}
                                weight={isActive ? 'fill' : 'regular'}
                            />
                        </button>
                    </Popover.Target>
                    <Popover.Dropdown p={0}>
                        <ColumnFilterPopover
                            isSortedAsc={
                                sortKey === col.key && sortDirection === 'asc'
                            }
                            isSortedDesc={
                                sortKey === col.key && sortDirection === 'desc'
                            }
                            filter={columnFilters[col.key]}
                            onSort={(dir) => handleSortDirect(col.key, dir)}
                            onFilter={(f) => handleFilter(col.key, f)}
                            onClose={() => setOpenFilterKey(null)}
                        />
                    </Popover.Dropdown>
                </Popover>
            );
        },
        [
            columnFilters,
            openFilterKey,
            hoveredHeaderKey,
            sortKey,
            sortDirection,
            handleSortDirect,
            handleFilter,
        ],
    );

    /**
     * Header-cell flex content: label + sort caret. Right-aligned (numeric)
     * columns put the caret BEFORE the label so the label text stays flush
     * with the numbers' right edge. The label wraps at word boundaries only —
     * its flex min-width is its longest word, which resolveColumnWidth
     * guarantees every column can fit, so words never break mid-word and never
     * paint over the neighbouring header. (The filter button is NOT in this
     * flow — it renders as an absolute overlay, see renderColumnFilter.)
     */
    /**
     * The header the explode toggle hangs off: the identity column. That is the first sticky
     * column in every grid that has one — the SKU / product column — because the control acts
     * on row identity, not on any one metric.
     */
    const explosionAnchorKey = useMemo(
        () =>
            (columnsProp.find((col) => col.sticky) ?? columnsProp[0])?.key,
        [columnsProp],
    );

    const renderHeaderCellContent = (col: DataSheetColumn<T>): ReactNode => {
        const label = (
            <span>{col.renderHeader ? col.renderHeader() : col.header}</span>
        );
        const explosionToggle =
            explosion?.available && col.key === explosionAnchorKey ? (
                <Tooltip
                    label={
                        explosion.error
                            ? explosion.error
                            : explosion.enabled
                              ? 'Showing base SKUs. Combo, customisation and clearance rows have been restated as the SKUs they are made of.'
                              : `Restate ${explosion.affectedRowCount} combo / variant row(s) as the base SKUs they are made of`
                    }
                    multiline
                    w={260}
                >
                    <ActionIcon
                        size="xs"
                        variant={explosion.enabled ? 'filled' : 'subtle'}
                        color={
                            explosion.error
                                ? 'orange'
                                : explosion.enabled
                                  ? 'blue'
                                  : 'gray'
                        }
                        disabled={!!explosion.error}
                        aria-label={explosion.label}
                        aria-pressed={explosion.enabled}
                        onClick={(event) => {
                            // The header cell is also the sort target.
                            event.stopPropagation();
                            explosion.onToggle(!explosion.enabled);
                        }}
                    >
                        <TreeStructure size={12} />
                    </ActionIcon>
                </Tooltip>
            ) : null;
        const sort = col.sortable ? (
            <SortIndicator
                direction={sortKey === col.key ? sortDirection : null}
            />
        ) : null;
        return col.align === 'right' ? (
            <>
                {sort}
                {label}
                {explosionToggle}
            </>
        ) : (
            <>
                {label}
                {sort}
                {explosionToggle}
            </>
        );
    };

    /**
     * Wraps the first column's content with the expand chevron (parents) or an indent
     * (children), so expandability costs consumers nothing in their column definitions.
     */
    const renderWithExpandAffordance = useCallback(
        (
            content: ReactNode,
            flat: { row: T; key: string; depth: number; hasSubRows: boolean },
        ): ReactNode => {
            if (flat.depth === 0 && !flat.hasSubRows) return content;
            return (
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        paddingLeft: flat.depth * 16,
                        minWidth: 0,
                        maxWidth: '100%',
                    }}
                >
                    {flat.hasSubRows ? (
                        // Indicator only — the whole row is the click target (handleRowClick),
                        // so this must not look like the sole affordance.
                        <span
                            aria-hidden="true"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 12,
                                flexShrink: 0,
                                color: tokens.colors.textMuted,
                                transform: expandedKeys.has(flat.key)
                                    ? 'rotate(90deg)'
                                    : 'none',
                                transition: 'transform 120ms ease',
                            }}
                        >
                            <CaretRight size={10} weight="bold" />
                        </span>
                    ) : (
                        <span style={{ width: 12, flexShrink: 0 }} />
                    )}
                    <span
                        style={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {content}
                    </span>
                </span>
            );
        },
        [expandedKeys],
    );

    const toggleRowExpanded = useCallback(
        (key: string, row: T) => {
            setExpandedKeys((previous) => {
                const next = new Set(previous);
                if (next.has(key)) {
                    next.delete(key);
                } else {
                    next.add(key);
                }
                onRowExpandChange?.(row, next.has(key));
                return next;
            });
        },
        [onRowExpandChange],
    );

    /**
     * Click behaviour for a body row: an expandable row toggles (the whole row is the
     * target, not just the chevron); anything else falls through to `onRowClick`.
     */
    const handleRowClick = useCallback(
        (
            flat: { row: T; key: string; hasSubRows: boolean },
            rowIndex: number,
        ) => {
            if (flat.hasSubRows) {
                toggleRowExpanded(flat.key, flat.row);
                return;
            }
            onRowClick?.(flat.row, rowIndex);
        },
        [onRowClick, toggleRowExpanded],
    );

    // Virtualizer scroll container ref
    const tableScrollRef = useRef<HTMLDivElement>(null);

    // Row virtualizer — always render; only ~20 rows in DOM regardless of dataset size.
    //
    // Rows are MEASURED, not assumed. `rowHeight` is the floor and the first guess, but a
    // two-line product cell or a wrapped name makes a row taller than that, and sizing the
    // off-screen spacers from the guess alone made the scrollable height change every time
    // the rendered window moved. Near the bottom that became a feedback loop with the
    // browser's scroll anchoring — the grid visibly shaking up and down. Measuring each
    // rendered row keeps the spacers honest; a 0px reading (row inside a hidden tab panel,
    // or jsdom) keeps the estimate instead of collapsing the row.
    const estimatedRowHeight = rowHeight ?? tokens.dimensions.rowHeight;
    const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
        count: flatRows.length,
        getScrollElement: () => tableScrollRef.current,
        estimateSize: () => estimatedRowHeight,
        getItemKey: (index) => flatRows[index]?.key ?? index,
        measureElement: (element, entry, instance) => {
            const measured = measureVirtualElement(element, entry, instance);
            return measured > 0 ? measured : estimatedRowHeight;
        },
        overscan: 15,
    });
    // Tighten vertical cell padding (header + body) when rows are compacted below the default height
    const compactCellPadY =
        rowHeight && rowHeight < tokens.dimensions.rowHeight
            ? 6
            : tokens.spacing.sm;
    const virtualItems = rowVirtualizer.getVirtualItems();
    const totalVirtualSize = rowVirtualizer.getTotalSize();
    const paddingTop =
        virtualItems.length > 0 ? (virtualItems[0]?.start ?? 0) : 0;
    const paddingBottom =
        virtualItems.length > 0
            ? totalVirtualSize -
              (virtualItems[virtualItems.length - 1]?.end ?? 0)
            : 0;

    // Sort columns by section: sticky → entity → metric (default) → grouping
    const SECTION_ORDER: Record<string, number> = {
        sticky: 0,
        entity: 1,
        metric: 2,
        grouping: 3,
    };
    const getSectionRank = (col: DataSheetColumn<T>) => {
        if (col.sticky) return SECTION_ORDER.sticky;
        if (col.section) return SECTION_ORDER[col.section] ?? 2;
        return 2; // metric by default
    };
    const columns = useMemo(() => {
        // Excel-style sort + filter are on by DEFAULT for every column across the app.
        // A column opts out with `sortable: false` / `filterable: false`.
        // A `'fit'` width is measured against the data here, once, so every later read of a
        // column's width - colgroup, sticky offsets, the table's min-width - sees a number.
        const withDefaults = columnsProp
            .filter((c) => !c.csvOnly)
            .map((c) => ({
            ...c,
            width: c.width === 'fit' ? fitColumnWidth(c, data) : c.width,
            sortable: c.sortable ?? true,
            filterable: c.filterable ?? true,
        }));
        const hasSections = withDefaults.some((c) => c.section || c.sticky);
        if (!hasSections) return withDefaults;
        return withDefaults.sort(
            (a, b) => getSectionRank(a) - getSectionRank(b),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsProp, data]);

    // Group columns by group header
    const columnGroups = useMemo(() => groupDataSheetColumns(columns), [columns]);

    const hasGroupHeaders = columns.some((col) => col.group);

    // Outer band: consecutive columns sharing a superGroup are spanned by one cell.
    const superGroups = useMemo(() => {
        const out: { name: string | null; columns: DataSheetColumn<T>[] }[] = [];
        let current: string | null = null;
        let cols: DataSheetColumn<T>[] = [];
        columns.forEach((col) => {
            const sg = col.superGroup || null;
            if (sg !== current && cols.length > 0) {
                out.push({ name: current, columns: cols });
                cols = [];
            }
            current = sg;
            cols.push(col);
        });
        if (cols.length > 0) out.push({ name: current, columns: cols });
        return out;
    }, [columns]);
    const hasSuperGroupHeaders = hasGroupHeaders && columns.some((col) => col.superGroup);
    /** Height of the group band(s) above the column header row. */
    const groupBandHeight = (hasGroupHeaders ? 30 : 0) + (hasSuperGroupHeaders ? 30 : 0);

    const renderSuperGroupRow = (): ReactNode => {
        if (!hasSuperGroupHeaders) return null;
        const stickyCount = columns.filter((c) => c.sticky).length;
        let idx = 0;
        return (
            <tr style={{ backgroundColor: tokens.colors.headerBg }}>
                {superGroups.map((sg, i) => {
                    const start = idx;
                    const end = idx + sg.columns.length - 1;
                    idx += sg.columns.length;
                    const fullySticky = end < stickyCount;
                    let left = 0;
                    if (fullySticky) {
                        for (let k = 0; k < start; k++) {
                            const c = columns[k];
                            if (c?.sticky) left += resolveColumnWidth(c);
                        }
                    }
                    return (
                        <th
                            key={`${sg.name ?? 'none'}-${i}`}
                            colSpan={sg.columns.length}
                            style={{
                                padding: `${tokens.spacing.xs}px ${tokens.spacing.md}px`,
                                height: 30,
                                borderBottom: `1px solid ${tokens.colors.border}`,
                                borderRight:
                                    i < superGroups.length - 1
                                        ? `2px solid ${tokens.colors.borderStrong}`
                                        : 'none',
                                textAlign: 'center',
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                color: tokens.colors.textSecondary,
                                position: fullySticky ? 'sticky' : 'relative',
                                left: fullySticky ? left : undefined,
                                zIndex: fullySticky ? 20 : 1,
                                backgroundColor: fullySticky
                                    ? tokens.colors.headerBgSticky
                                    : tokens.colors.headerBg,
                            }}
                        >
                            {sg.name ?? ''}
                        </th>
                    );
                })}
            </tr>
        );
    };

    // Indices of columns that sit on a group boundary — the last column of each group
    // except the final one. Used to render a stronger vertical divider so users can see
    // where one group (e.g. month) ends and the next begins.
    const groupBoundaryIndices = useMemo(() => groupBoundarySet(columnGroups, hasGroupHeaders), [columnGroups, hasGroupHeaders]);
    // The capture (the emailed image and the exported table) leaves out action columns: a
    // header-less column holds row actions (Approve, Save, …), not row information. Same
    // rule as the CSV download.
    const captureColumns = useMemo(() => columns.filter((col) => (col.csvHeader ?? col.header) !== ''), [columns]);
    const captureColumnGroups = useMemo(() => groupDataSheetColumns(captureColumns), [captureColumns]);
    const captureBoundaryIndices = useMemo(() => groupBoundarySet(captureColumnGroups, captureColumns.some((col) => col.group)), [captureColumnGroups, captureColumns]);
    const hasData = displayData.length > 0;
    const showEmptyState = !isLoading && !hasData && columns.length > 0;
    const useInternalTable = columns.length > 0 && !children;

    // Locks each column's width up front so `table-layout: fixed` (below) sizes columns from
    // these declarations instead of from the currently-visible rows. Without this, virtualization
    // swaps row content in/out on scroll and an `auto` layout re-solves widths every frame → flicker.
    //
    // A column that declares no width falls back to DEFAULT_COLUMN_WIDTH rather than `auto`: under
    // `table-layout: fixed`, `auto` means "take an equal share of the container", so a 40-column
    // table would crush every column to a few pixels instead of overflowing.
    const renderColGroup = () => (
        <colgroup>
            {columns.map((col) => (
                <col key={col.key} style={{ width: resolveColumnWidth(col) }} />
            ))}
        </colgroup>
    );

    // Sum of the declared widths. Set as the table's min-width so a table wider than its container
    // overflows into the scroll wrapper (which already has overflowX: auto) instead of compressing.
    // Below that sum the table still stretches to 100%, so narrow tables fill their real estate.
    const totalColumnsWidth = useMemo(
        () => columns.reduce((sum, col) => sum + resolveColumnWidth(col), 0),
        [columns],
    );

    const tableStyle = {
        width: '100%',
        minWidth: totalColumnsWidth,
        borderCollapse: 'collapse' as const,
        tableLayout: 'fixed' as const,
    };

    /**
     * Totals answer for what is on screen. `displayData` is the set after the view spec's
     * filters and the search box but before the page slice — which is exactly the rule a
     * spreadsheet uses: filtering changes the total, scrolling does not.
     */
    const effectiveAggregateRow = useMemo(
        () => (aggregateFn ? aggregateFn(displayData) : aggregateRow),
        [aggregateFn, aggregateRow, displayData],
    );

    const hasAggregateRow =
        !!effectiveAggregateRow && Object.keys(effectiveAggregateRow).length > 0;

    const renderAggregateRow = () => (
        <tr>
            {columns.map((col, idx) => {
                let leftOffset = 0;
                if (col.sticky) {
                    for (let i = 0; i < idx; i++) {
                        if (columns[i].sticky) {
                            leftOffset += resolveColumnWidth(columns[i]);
                        }
                    }
                }
                const isLastSticky =
                    col.sticky &&
                    (idx === columns.length - 1 || !columns[idx + 1]?.sticky);
                const value = effectiveAggregateRow?.[col.key];
                return (
                    <th
                        key={col.key}
                        style={{
                            padding: `${tokens.spacing.xs}px ${tokens.spacing.md}px`,
                            borderTop: `2px solid ${tokens.colors.border}`,
                            borderBottom: `1px solid ${tokens.colors.border}`,
                            borderRight: groupBoundaryIndices.has(idx)
                                ? `1px solid ${tokens.colors.borderStrong}`
                                : idx < columns.length - 1
                                  ? `1px solid ${tokens.colors.borderSubtle}`
                                  : 'none',
                            textAlign: col.align || 'left',
                            position: col.sticky ? 'sticky' : 'relative',
                            left: col.sticky ? leftOffset : undefined,
                            zIndex: col.sticky ? 20 : 1,
                            backgroundColor: col.sticky
                                ? tokens.colors.headerBgSticky
                                : tokens.colors.headerBg,
                            boxShadow: isLastSticky
                                ? '4px 0 8px rgba(0,0,0,0.08)'
                                : undefined,
                            fontSize: 12,
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            color: tokens.colors.textPrimary,
                        }}
                    >
                        {value ?? null}
                    </th>
                );
            })}
        </tr>
    );

    // =========================================================================
    // VIEW CAPTURE ("table image") — see the captureRef prop doc.
    // =========================================================================
    const [captureRequest, setCaptureRequest] = useState<{
        kind: 'image' | 'data';
        maxRows: number;
        expansion?: DataSheetCaptureExpansion;
        resolve: (result: DataSheetCaptureResult | DataSheetCaptureData) => void;
        reject: (error: Error) => void;
    } | null>(null);
    const captureNodeRef = useRef<HTMLDivElement | null>(null);

    /**
     * The FULL visual-row list: the same walk the on-screen body uses (parents interleaved
     * with the children of expanded ones), but over `displayData` instead of the page slice
     * — so "capture until row 150" works while the screen shows 100 per page. Group rows
     * and expanded children each count as one visual row, exactly as seen.
     */
    const captureView = useMemo(() => {
        if (!captureRequest) {
            return {
                rows: [] as Array<{
                    row: T;
                    key: string;
                    depth: number;
                    hasSubRows: boolean;
                }>,
                expansion: 'NONE' as DataSheetCaptureExpansion,
            };
        }
        const flattened: Array<{
            row: T;
            key: string;
            depth: number;
            hasSubRows: boolean;
        }> = [];
        const override = captureRequest.expansion;
        const overrideKeys = Array.isArray(override) ? new Set(override) : null;
        const isExpanded = (key: string): boolean => {
            if (override === 'ALL') return true;
            if (override === 'NONE') return false;
            if (overrideKeys) return overrideKeys.has(key);
            return expandedKeys.has(key);
        };
        let parentCount = 0;
        const expandedParentKeys: string[] = [];
        const walk = (rows: T[], depth: number, parentKey: string): void => {
            rows.forEach((row, index) => {
                const ownKey = getRowKey ? getRowKey(row, index) : String(index);
                const key = parentKey ? `${parentKey}__${ownKey}` : ownKey;
                const subRows = getSubRows?.(row);
                const hasSubRows = !!subRows && subRows.length > 0;
                flattened.push({ row, key, depth, hasSubRows });
                if (hasSubRows) {
                    parentCount++;
                    if (isExpanded(key)) {
                        expandedParentKeys.push(key);
                        walk(subRows, depth + 1, key);
                    }
                }
            });
        };
        walk(displayData, 0, '');
        // The state this capture actually rendered with, in replayable form: the two
        // common cases collapse to 'ALL'/'NONE'; a partially opened view keeps its keys.
        const expansion: DataSheetCaptureExpansion =
            expandedParentKeys.length === 0
                ? 'NONE'
                : expandedParentKeys.length === parentCount
                  ? 'ALL'
                  : expandedParentKeys;
        return { rows: flattened, expansion };
    }, [captureRequest, displayData, getSubRows, getRowKey, expandedKeys]);
    const captureVisualRows = captureView.rows;

    /** The grid's own capture handle — the email control reads it; `captureRef` mirrors it for parents. */
    const captureHandleRef = useRef<DataSheetCaptureHandle | null>(null);
    useEffect(() => {
        const startCapture = <T,>(
            kind: 'image' | 'data',
            options?: CaptureOptions,
        ): Promise<T> =>
            new Promise<T>((resolve, reject) => {
                setCaptureRequest((prev) => {
                    if (prev) {
                        reject(new Error('A capture is already in progress'));
                        return prev;
                    }
                    return {
                        kind,
                        maxRows: Math.max(1, options?.maxVisualRows ?? 30),
                        expansion: options?.expansion,
                        resolve: resolve as (
                            result: DataSheetCaptureResult | DataSheetCaptureData,
                        ) => void,
                        reject,
                    };
                });
            });
        const handle: DataSheetCaptureHandle = {
            captureViewImage: (options) =>
                startCapture<DataSheetCaptureResult>('image', options),
            captureViewData: (options) =>
                startCapture<DataSheetCaptureData>('data', options),
        };
        captureHandleRef.current = handle;
        if (captureRef) captureRef.current = handle;
        return () => {
            captureHandleRef.current = null;
            if (captureRef) captureRef.current = null;
        };
    }, [captureRef]);

    // ── Email this table (see the enableEmail prop doc) ─────────────────────
    const [emailOpen, setEmailOpen] = useState(false);
    const appContext = useContext(AppProviderContext);
    const currentUserEmail = appContext?.currentUser?.email ?? '';
    const emailEnabled = enableEmail && contentType === 'table' && columnsProp.length > 0;
    const emailDefaultName = emailName ?? (typeof title === 'string' && title.trim() ? title.trim() : 'Table');
    // Registered under its email name so a scheduled email can reopen this page and find this
    // sheet (see utils/headlessCapture). Refs keep the entry live without re-registering.
    const isLoadingRef = useRef(isLoading);
    isLoadingRef.current = isLoading;
    const rowCountRef = useRef(0);
    rowCountRef.current = displayData.length;
    const [captureKey, setCaptureKey] = useState<string | null>(null);
    useEffect(() => {
        if (!emailEnabled) return;
        const { key, unregister } = registerCaptureSheet(emailDefaultName, {
            handle: () => captureHandleRef.current,
            isLoading: () => isLoadingRef.current,
            rowCount: () => rowCountRef.current,
        });
        setCaptureKey(key);
        return () => {
            unregister();
            setCaptureKey(null);
        };
    }, [emailEnabled, emailDefaultName]);
    const emailControl = emailEnabled ? (
        <Tooltip label="Email this table as it stands" withArrow>
            <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Email this table"
                onClick={() => setEmailOpen(true)}
                style={{
                    height: tokens.dimensions.controlHeight,
                    width: tokens.dimensions.controlHeight,
                }}
            >
                <EnvelopeSimple size={16} />
            </ActionIcon>
        </Tooltip>
    ) : null;
    const captureOrFail = <R,>(run: (handle: DataSheetCaptureHandle) => Promise<R>): Promise<R> => {
        const handle = captureHandleRef.current;
        if (!handle) return Promise.reject(new Error('The table is not ready to capture'));
        return run(handle);
    };

    // Rasterize once the off-screen copy has painted; double-rAF lets fonts/layout settle.
    useEffect(() => {
        if (!captureRequest) return;
        let cancelled = false;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const node = captureNodeRef.current;
                if (cancelled) return;
                if (!node) {
                    captureRequest.reject(new Error('Capture node not mounted'));
                    setCaptureRequest(null);
                    return;
                }
                if (captureRequest.kind === 'data') {
                    // Serialize the SAME off-screen render the image path rasterizes:
                    // per-cell display text (innerText, so a group cell's stacked label
                    // reads "Bento OG · 18 SKUs" instead of gluing) plus any non-default
                    // paint, read back from computed styles — whatever the grid painted
                    // is what the export carries.
                    try {
                        const isDefaultBg = (bg: string | undefined): boolean =>
                            !bg ||
                            bg === 'rgba(0, 0, 0, 0)' ||
                            bg === 'transparent' ||
                            bg === 'rgb(255, 255, 255)';
                        const readCells = (
                            rowEl: Element,
                        ): DataSheetCaptureCell[] => {
                            // Row-level paint (ViewSpec highlights sit on the <tr>) does
                            // NOT show up in a cell's own computed background.
                            const rowBg = window.getComputedStyle(
                                rowEl as HTMLElement,
                            ).backgroundColor;
                            // A group row's stacked label reads as one ("Bento OG · 18 SKUs");
                            // a leaf cell's value is its first line — the strong one on
                            // screen — and the lines under it are subtext, carried in
                            // `lines` for columns that split them out (captureDetailHeader).
                            const isGroupRow = rowEl.getAttribute('data-capture-group') === 'true';
                            return [...rowEl.querySelectorAll('td,th')].map((cellEl) => {
                                const el = cellEl as HTMLElement;
                                const style = window.getComputedStyle(el);
                                // Tone colors (red/amber DoH etc.) are painted on SPANS
                                // inside the cell, not the td. An XLSX cell has ONE font,
                                // so take the color of the FIRST text-bearing LEAF in
                                // reading order — the cell's primary line (the SKU code,
                                // the tone-colored number), never a stacked subtext.
                                let color = style.color;
                                let bg = style.backgroundColor;
                                let colorPicked = false;
                                for (const child of el.querySelectorAll('*')) {
                                    const childEl = child as HTMLElement;
                                    const hasText = !!childEl.textContent?.trim();
                                    if (!hasText) continue;
                                    const childStyle = window.getComputedStyle(childEl);
                                    const isLeaf = childEl.childElementCount === 0;
                                    if (!colorPicked && isLeaf) {
                                        color = childStyle.color;
                                        colorPicked = true;
                                    }
                                    if (
                                        isDefaultBg(bg) &&
                                        !isDefaultBg(childStyle.backgroundColor)
                                    ) {
                                        bg = childStyle.backgroundColor;
                                    }
                                }
                                if (isDefaultBg(bg) && !isDefaultBg(rowBg)) bg = rowBg;
                                const typed = el.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input,textarea,select');
                                const textLines = (el.innerText.trim() || typed?.value || '')
                                    .split('\n')
                                    .map((line) => line.trim())
                                    .filter(Boolean);
                                return {
                                    text: isGroupRow ? textLines.join(' · ') : (textLines[0] ?? ''),
                                    ...(textLines.length > 1
                                        ? { lines: textLines }
                                        : {}),
                                    ...(isDefaultBg(bg) ? {} : { bg }),
                                    color,
                                };
                            });
                        };
                        const bodyRows = [
                            ...node.querySelectorAll('tbody tr'),
                        ].map(readCells);
                        const aggregateEl = hasAggregateRow
                            ? node.querySelector('thead tr:last-child')
                            : null;
                        captureRequest.resolve({
                            columns: captureColumns.map((col) => ({
                                header: col.header,
                                ...(col.group ? { group: col.group } : {}),
                                ...(col.captureDetailHeader
                                    ? { detailHeader: col.captureDetailHeader }
                                    : {}),
                            })),
                            ...(aggregateEl
                                ? { aggregateRow: readCells(aggregateEl) }
                                : {}),
                            rows: bodyRows,
                            visualRowCount: Math.min(
                                captureRequest.maxRows,
                                captureVisualRows.length,
                            ),
                            totalVisualRows: captureVisualRows.length,
                            expansion: captureView.expansion,
                        });
                    } catch (error) {
                        captureRequest.reject(
                            error instanceof Error ? error : new Error(String(error)),
                        );
                    }
                    setCaptureRequest(null);
                    return;
                }
                // JPEG, not PNG: a wide 2x-scale table as PNG can blow the API's 2MB
                // JSON body cap; at quality .92 text stays crisp at a fraction of the size.
                //
                // The ratio backs off for very large captures: browsers cap a canvas
                // dimension around ~32k px, and a 200-row wide table at 2x sails past it —
                // the rasterize then hangs or silently produces an empty image.
                const rect = node.getBoundingClientRect();
                const maxDimension = Math.max(rect.width, rect.height, 1);
                const pixelRatio = Math.max(1, Math.min(2, 9_000 / maxDimension));
                const timeout = new Promise<never>((_, timeoutReject) => {
                    setTimeout(
                        () => timeoutReject(new Error('Capture timed out — try fewer rows')),
                        45_000,
                    );
                });
                Promise.race([
                    toJpeg(node, {
                        pixelRatio,
                        quality: 0.92,
                        backgroundColor: '#ffffff',
                    }),
                    timeout,
                ])
                    .then((dataUrl) => {
                        if (cancelled) return;
                        captureRequest.resolve({
                            dataUrl,
                            visualRowCount: Math.min(
                                captureRequest.maxRows,
                                captureVisualRows.length,
                            ),
                            totalVisualRows: captureVisualRows.length,
                            expansion: captureView.expansion,
                        });
                    })
                    .catch((error: unknown) => {
                        if (cancelled) return;
                        captureRequest.reject(
                            error instanceof Error
                                ? error
                                : new Error(String(error)),
                        );
                    })
                    .finally(() => {
                        if (!cancelled) setCaptureRequest(null);
                    });
            });
        });
        return () => {
            cancelled = true;
        };
        // captureVisualRows is derived from captureRequest — rerunning on it would double-fire.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [captureRequest]);

    /**
     * The off-screen static copy: same colgroup, group-header bands, header labels,
     * aggregate (Total) row and cell renderers as the live body — colors and formatting
     * come from the very same `col.render` calls — with virtualization, stickiness and
     * interactivity stripped. Parked far off-viewport; unmounted the moment the PNG exists.
     */
    const capturePortal = captureRequest
        ? createPortal(
              // Two layers on purpose: html-to-image CLONES the captured node with its inline
              // styles, so capturing the fixed/-100000px wrapper itself reproduces that offset
              // inside the clone and yields a blank image. The wrapper hides; the statically
              // positioned inner node is what gets rasterized.
              <div
                  aria-hidden="true"
                  style={{
                      position: 'fixed',
                      left: -100_000,
                      top: 0,
                      zIndex: -1,
                  }}
              >
              <div
                  ref={captureNodeRef}
                  style={{
                      backgroundColor: '#ffffff',
                      padding: 12,
                      width: 'max-content',
                  }}
              >
                  <table style={{ ...tableStyle, width: totalColumnsWidth }}>
                      {renderColGroup()}
                      <thead>
                          {renderSuperGroupRow()}
                          {hasGroupHeaders && (
                              <tr style={{ backgroundColor: tokens.colors.headerBg }}>
                                  {captureColumnGroups.map((group, groupIdx) => (
                                      <th
                                          key={`${group.name ?? 'ungrouped'}-${groupIdx}`}
                                          colSpan={group.columns.length}
                                          style={{
                                              padding: `${tokens.spacing.xs}px ${tokens.spacing.md}px`,
                                              borderBottom: `1px solid ${tokens.colors.borderSubtle}`,
                                              borderRight:
                                                  groupIdx < captureColumnGroups.length - 1
                                                      ? `1px solid ${tokens.colors.borderStrong}`
                                                      : 'none',
                                              textAlign: 'center',
                                              fontSize: 11,
                                              fontWeight: 700,
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.04em',
                                              color: tokens.colors.textSecondary,
                                          }}
                                      >
                                          {group.name ?? ''}
                                      </th>
                                  ))}
                              </tr>
                          )}
                          <tr style={{ backgroundColor: tokens.colors.headerBg }}>
                              {captureColumns.map((col, colIdx) => (
                                  <th
                                      key={col.key}
                                      style={{
                                          padding: `${tokens.spacing.xs}px ${tokens.spacing.md}px`,
                                          borderBottom: `2px solid ${tokens.colors.border}`,
                                          borderRight: captureBoundaryIndices.has(colIdx)
                                              ? `1px solid ${tokens.colors.borderStrong}`
                                              : colIdx < captureColumns.length - 1
                                                ? `1px solid ${tokens.colors.borderSubtle}`
                                                : 'none',
                                          textAlign: col.align || 'left',
                                          fontSize: 11,
                                          fontWeight: 600,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.03em',
                                          color: tokens.colors.textSecondary,
                                      }}
                                  >
                                      {col.renderHeader ? col.renderHeader() : col.header}
                                  </th>
                              ))}
                          </tr>
                          {hasAggregateRow && renderAggregateRow()}
                      </thead>
                      <tbody>
                          {captureVisualRows
                              .slice(0, captureRequest.maxRows)
                              .map((flat, index) => {
                                  const rowClassName = getRowClassName?.(
                                      flat.row,
                                      index,
                                  );
                                  const highlightBg = highlightByKey.get(flat.key)
                                      ? HIGHLIGHT_BG[highlightByKey.get(flat.key)!]
                                      : undefined;
                                  return (
                                      <tr
                                          data-capture-group={flat.hasSubRows ? 'true' : undefined}
                                          key={flat.key}
                                          className={rowClassName}
                                          style={{ backgroundColor: highlightBg }}
                                      >
                                          {captureColumns.map((col, colIdx) => {
                                              const value = flat.row[col.key];
                                              const rendered = col.render
                                                  ? col.render(value, flat.row, index)
                                                  : (value?.toString() ?? '—');
                                              const content =
                                                  colIdx === 0
                                                      ? renderWithExpandAffordance(
                                                            rendered,
                                                            flat,
                                                        )
                                                      : rendered;
                                              return (
                                                  <td
                                                      key={col.key}
                                                      className={bodyCellClassName(col, colIdx === 0 ? rowClassName : undefined)}
                                                    onMouseEnter={revealClippedCellText}
                                                      style={{
                                                          padding: `${compactCellPadY}px ${tokens.spacing.md}px`,
                                                          borderBottom: `1px solid ${tokens.colors.borderSubtle}`,
                                                          borderRight:
                                                              captureBoundaryIndices.has(
                                                                  colIdx,
                                                              )
                                                                  ? `1px solid ${tokens.colors.borderStrong}`
                                                                  : colIdx <
                                                                      captureColumns.length - 1
                                                                    ? `1px solid ${tokens.colors.borderSubtle}`
                                                                    : 'none',
                                                          textAlign: col.align || 'left',
                                                          verticalAlign: 'middle',
                                                          ...tokens.typography.cell,
                                                          color: tokens.colors
                                                              .textPrimary,
                                                      }}
                                                  >
                                                      {content}
                                                  </td>
                                              );
                                          })}
                                      </tr>
                                  );
                              })}
                      </tbody>
                  </table>
                  {captureVisualRows.length > captureRequest.maxRows && (
                      <div
                          style={{
                              padding: `${tokens.spacing.xs}px ${tokens.spacing.md}px`,
                              fontSize: 11,
                              color: tokens.colors.textMuted,
                          }}
                      >
                          + {captureVisualRows.length - captureRequest.maxRows} more
                          row(s) not shown
                      </div>
                  )}
              </div>
              </div>,
              document.body,
          )
        : null;

    // Bare mode: render just the table without wrapper
    // When maxHeight is 'auto', don't use ScrollArea - let parent handle scrolling
    // This allows sticky headers to work properly
    if (bare && useInternalTable) {
        const tableElement = (
            <table style={tableStyle}>
                {renderColGroup()}
                {/* Column Group Headers (if any) */}
                {hasGroupHeaders &&
                    (() => {
                        // Calculate sticky column info for group headers
                        const stickyColumns = columns.filter((c) => c.sticky);
                        const stickyColumnCount = stickyColumns.length;

                        // Track column index as we iterate through groups
                        let columnIndex = 0;

                        return (
                            <thead
                                style={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 20,
                                }}
                            >
                                {renderSuperGroupRow()}
<tr
                                    style={{
                                        backgroundColor: tokens.colors.headerBg,
                                    }}
                                >
                                    {columnGroups.map((group, gIdx) => {
                                        const groupStartIndex = columnIndex;
                                        const groupEndIndex =
                                            columnIndex +
                                            group.columns.length -
                                            1;
                                        columnIndex += group.columns.length;

                                        // Check if this group contains any sticky columns
                                        const containsStickyColumns =
                                            groupStartIndex < stickyColumnCount;
                                        // Check if this group contains ONLY sticky columns
                                        const isFullySticky =
                                            groupEndIndex < stickyColumnCount;
                                        // Check if this is the last group that contains sticky columns
                                        const isLastStickyGroup =
                                            containsStickyColumns &&
                                            groupEndIndex >=
                                                stickyColumnCount - 1;

                                        // Calculate left offset for sticky group headers
                                        let leftOffset = 0;
                                        if (
                                            containsStickyColumns &&
                                            isFullySticky
                                        ) {
                                            for (
                                                let i = 0;
                                                i < groupStartIndex;
                                                i++
                                            ) {
                                                const col = columns[i];
                                                if (col?.sticky) {
                                                    leftOffset +=
                                                        resolveColumnWidth(col);
                                                }
                                            }
                                        }

                                        return (
                                            <th
                                                key={gIdx}
                                                colSpan={group.columns.length}
                                                title={group.tooltip}
                                                style={{
                                                    padding: `${tokens.spacing.xs}px ${tokens.spacing.md}px`,
                                                    borderBottom: `1px solid ${tokens.colors.border}`,
                                                    borderRight:
                                                        gIdx <
                                                        columnGroups.length - 1
                                                            ? `1px solid ${tokens.colors.borderStrong}`
                                                            : 'none',
                                                    textAlign: 'center',
                                                    ...tokens.typography.header,
                                                    color: tokens.colors
                                                        .textSecondary,
                                                    // Make sticky if this group contains only sticky columns
                                                    position: isFullySticky
                                                        ? 'sticky'
                                                        : 'relative',
                                                    left: isFullySticky
                                                        ? leftOffset
                                                        : undefined,
                                                    zIndex: isFullySticky
                                                        ? 20
                                                        : 1,
                                                    // Use opaque background for sticky groups
                                                    backgroundColor:
                                                        isFullySticky
                                                            ? tokens.colors
                                                                  .headerBgSticky
                                                            : tokens.colors
                                                                  .headerBg,
                                                    boxShadow:
                                                        isLastStickyGroup &&
                                                        isFullySticky
                                                            ? '4px 0 8px rgba(0,0,0,0.08)'
                                                            : undefined,
                                                }}
                                            >
                                                {group.name || ''}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                        );
                    })()}

                {/* Column Headers */}
                <thead
                    style={{
                        position: 'sticky',
                        top: groupBandHeight,
                        zIndex: 20,
                        backgroundColor: tokens.colors.headerBg,
                    }}
                >
                    <tr style={{ backgroundColor: tokens.colors.headerBg }}>
                        {columns.map((col, idx) => {
                            // Calculate left position for sticky columns
                            let leftOffset = 0;
                            if (col.sticky) {
                                for (let i = 0; i < idx; i++) {
                                    if (columns[i].sticky) {
                                        leftOffset += resolveColumnWidth(
                                            columns[i],
                                        );
                                    }
                                }
                            }
                            const isLastSticky =
                                col.sticky &&
                                (idx === columns.length - 1 ||
                                    !columns[idx + 1]?.sticky);

                            return (
                                <th
                                    key={col.key}
                                    onClick={
                                        col.sortable
                                            ? () => handleSort(col.key)
                                            : undefined
                                    }
                                    onMouseEnter={() =>
                                        setHoveredHeaderKey(col.key)
                                    }
                                    onMouseLeave={() =>
                                        setHoveredHeaderKey(null)
                                    }
                                    style={{
                                        padding: `${compactCellPadY}px ${tokens.spacing.md}px`,
                                        borderBottom: `1px solid ${tokens.colors.border}`,
                                        borderRight: groupBoundaryIndices.has(
                                            idx,
                                        )
                                            ? `1px solid ${tokens.colors.borderStrong}`
                                            : idx < columns.length - 1
                                              ? `1px solid ${tokens.colors.borderSubtle}`
                                              : 'none',
                                        textAlign: col.align || 'left',
                                        width: resolveColumnWidth(col),
                                        minWidth: resolveColumnWidth(col),
                                        position: col.sticky
                                            ? 'sticky'
                                            : 'relative',
                                        left: col.sticky
                                            ? leftOffset
                                            : undefined,
                                        zIndex: col.sticky ? 20 : 1,
                                        // Use opaque background for sticky columns to prevent text bleed-through
                                        backgroundColor: col.sticky
                                            ? tokens.colors.headerBgSticky
                                            : tokens.colors.headerBg,
                                        cursor: col.sortable
                                            ? 'pointer'
                                            : 'default',
                                        userSelect: 'none',
                                        boxShadow: isLastSticky
                                            ? '4px 0 8px rgba(0,0,0,0.08)'
                                            : undefined,
                                        ...tokens.typography.header,
                                        color: tokens.colors.textSecondary,
                                    }}
                                >
                                    <Group
                                        wrap="nowrap"
                                        style={{
                                            gap: tokens.spacing.xxs,
                                            justifyContent:
                                                col.align === 'right'
                                                    ? 'flex-end'
                                                    : col.align === 'center'
                                                      ? 'center'
                                                      : 'flex-start',
                                            // Backstop: if a header word still
                                            // out-measures the column, clip it
                                            // rather than bleed over the
                                            // neighbouring opaque header. A
                                            // control column (empty header)
                                            // has no label to bleed, and its
                                            // checkbox must stay whole.
                                            overflow:
                                                col.header === ''
                                                    ? 'visible'
                                                    : 'hidden',
                                        }}
                                    >
                                        {renderHeaderCellContent(col)}
                                    </Group>
                                    {renderColumnFilter(col)}
                                </th>
                            );
                        })}
                    </tr>
                    {hasAggregateRow && renderAggregateRow()}
                </thead>

                {/* Body */}
                <tbody>
                    {/* Loading State */}
                    {isLoading && (
                        <>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <SkeletonRow key={i} columns={columns.length} />
                            ))}
                        </>
                    )}

                    {/* Empty State */}
                    {showEmptyState && (
                        <tr>
                            <td
                                colSpan={columns.length}
                                style={{
                                    padding: `${tokens.spacing.lg * 2}px ${tokens.spacing.md}px`,
                                    textAlign: 'center',
                                    color: tokens.colors.textMuted,
                                    fontSize: 13,
                                }}
                            >
                                {emptyState || 'No data available'}
                            </td>
                        </tr>
                    )}

                    {/* Data Rows — virtualized: only visible rows are in the DOM */}
                    {!isLoading && hasData && (
                        <>
                            {paddingTop > 0 && (
                                <tr aria-hidden="true">
                                    <td
                                        colSpan={columns.length}
                                        style={{
                                            height: paddingTop,
                                            padding: 0,
                                            border: 'none',
                                        }}
                                    />
                                </tr>
                            )}
                            {virtualItems.map((virtualRow) => {
                                const flat = flatRows[virtualRow.index];
                                const row = flat.row;
                                const rowKey = flat.key;
                                const rowClassName = getRowClassName?.(
                                    row,
                                    virtualRow.index,
                                );
                                const highlightBg = highlightByKey.get(rowKey)
                                    ? HIGHLIGHT_BG[highlightByKey.get(rowKey)!]
                                    : undefined;
                                return (
                                    <tr
                                        key={rowKey}
                                        ref={rowVirtualizer.measureElement}
                                        data-index={virtualRow.index}
                                        className={rowClassName}
                                        onClick={
                                            onRowClick || flat.hasSubRows
                                                ? () =>
                                                      handleRowClick(
                                                          flat,
                                                          virtualRow.index,
                                                      )
                                                : undefined
                                        }
                                        style={{
                                            height: estimatedRowHeight,
                                            cursor:
                                                onRowClick || flat.hasSubRows
                                                    ? 'pointer'
                                                    : 'default',
                                            backgroundColor: highlightBg,
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                                tokens.colors.hoverRow;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                                highlightBg ?? '';
                                        }}
                                    >
                                        {columns.map((col, colIdx) => {
                                            const value = row[col.key];
                                            const rendered = col.render
                                                ? col.render(
                                                      value,
                                                      row,
                                                      virtualRow.index,
                                                  )
                                                : (value?.toString() ?? '—');
                                            const content =
                                                colIdx === 0
                                                    ? renderWithExpandAffordance(
                                                          rendered,
                                                          flat,
                                                      )
                                                    : rendered;
                                            let leftOffset = 0;
                                            if (col.sticky) {
                                                for (
                                                    let i = 0;
                                                    i < colIdx;
                                                    i++
                                                ) {
                                                    if (columns[i].sticky)
                                                        leftOffset +=
                                                            resolveColumnWidth(
                                                                columns[i],
                                                            );
                                                }
                                            }
                                            const isLastSticky =
                                                col.sticky &&
                                                (colIdx ===
                                                    columns.length - 1 ||
                                                    !columns[colIdx + 1]
                                                        ?.sticky);
                                            return (
                                                <td
                                                    key={col.key}
                                                    className={bodyCellClassName(col, colIdx === 0 ? rowClassName : undefined)}
                                                    onMouseEnter={revealClippedCellText}
                                                    style={{
                                                        padding: `${compactCellPadY}px ${tokens.spacing.md}px`,
                                                        borderBottom: `1px solid ${tokens.colors.borderSubtle}`,
                                                        borderRight:
                                                            groupBoundaryIndices.has(
                                                                colIdx,
                                                            )
                                                                ? `1px solid ${tokens.colors.borderStrong}`
                                                                : colIdx <
                                                                    columns.length -
                                                                        1
                                                                  ? `1px solid ${tokens.colors.borderSubtle}`
                                                                  : 'none',
                                                        textAlign:
                                                            col.align || 'left',
                                                        height: estimatedRowHeight,
                                                        verticalAlign: 'middle',
                                                        position: col.sticky
                                                            ? 'sticky'
                                                            : 'relative',
                                                        left: col.sticky
                                                            ? leftOffset
                                                            : undefined,
                                                        zIndex: col.sticky
                                                            ? 10
                                                            : 1,
                                                        backgroundColor:
                                                            col.sticky
                                                                ? tokens.colors
                                                                      .surfaceSticky
                                                                : 'inherit',
                                                        boxShadow: isLastSticky
                                                            ? '4px 0 8px rgba(0,0,0,0.08)'
                                                            : undefined,
                                                        ...tokens.typography
                                                            .cell,
                                                        color: tokens.colors
                                                            .textPrimary,
                                                    }}
                                                >
                                                    {content}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                            {paddingBottom > 0 && (
                                <tr aria-hidden="true">
                                    <td
                                        colSpan={columns.length}
                                        style={{
                                            height: paddingBottom,
                                            padding: 0,
                                            border: 'none',
                                        }}
                                    />
                                </tr>
                            )}
                        </>
                    )}
                </tbody>
            </table>
        );

        // Bare mode scroll wrapper — plain div so virtualizer can attach to the scroll element
        // tableScrollRef must be attached here (not the forwarded ref) so the virtualizer can measure the container
        if (maxHeight === 'auto') {
            return (
                <div ref={tableScrollRef} className={className}>
                    {tableElement}
                    {capturePortal}
                </div>
            );
        }

        return (
            <div
                ref={tableScrollRef}
                className={className}
                style={{
                    maxHeight: maxHeight || undefined,
                    overflowY: 'auto',
                    overflowX: 'auto',
                    // The virtualizer corrects scroll position itself when a row
                    // above the viewport is measured; leaving the browser's own
                    // anchoring on makes two mechanisms fight over the same shift.
                    overflowAnchor: 'none',
                }}
            >
                {tableElement}
                {capturePortal}
            </div>
        );
    }

    return (
        /* Wrapper needed so the "Add to Chat" chip can sit outside the Box's overflow:hidden boundary */
        <div
            ref={containerRef as React.RefObject<HTMLDivElement>}
            className={className}
            style={{
                position: 'relative',
                flex: '1 1 auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {capturePortal}
            {/* Add to Chat chip — floats above the top-right corner of the selection ring */}
            {isSelected && onAddToChat && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onAddToChat();
                        setIsSelected(false);
                    }}
                    style={{
                        position: 'absolute',
                        top: -24,
                        right: 0,
                        zIndex: 20,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                        padding: '2px 7px',
                        fontSize: 10,
                        fontWeight: 500,
                        color: '#6366f1',
                        background: 'rgba(99,102,241,0.06)',
                        border: '1px solid rgba(99,102,241,0.22)',
                        borderRadius: 5,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 4px rgba(99,102,241,0.12)',
                        lineHeight: 1.4,
                    }}
                >
                    <ChatCircleDots size={10} weight="duotone" />
                    Add to Chat
                </button>
            )}
            <Box
                style={{
                    backgroundColor: tokens.colors.surface,
                    borderRadius: bare ? 0 : 12,
                    border: bare
                        ? 'none'
                        : isSelected && onAddToChat
                          ? '1px solid rgba(99,102,241,0.5)'
                          : `1px solid ${tokens.colors.border}`,
                    outline:
                        isSelected && onAddToChat
                            ? '2px solid rgba(99,102,241,0.25)'
                            : 'none',
                    outlineOffset: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    position: 'relative',
                    transition: 'border-color 0.15s ease, outline 0.15s ease',
                    flex: '1 1 auto',
                }}
            >
                {/* Sheet Tabs */}
                {tabs && tabs.length > 0 && (
                    <SheetTabStrip
                        tabs={tabs}
                        activeTab={activeTab}
                        onTabChange={onTabChange}
                    />
                )}

                {/* Header Bar */}
                {!hideHeaderBar && (
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            // Wrap instead of overflowing: the card clips
                            // (overflow: hidden), so on a narrow viewport
                            // non-wrapping controls are not just ugly, they are
                            // unreachable.
                            flexWrap: 'wrap',
                            minHeight: tokens.dimensions.headerBarHeight,
                            padding: `${tokens.spacing.xs}px ${tokens.spacing.md}px`,
                            borderBottom: `1px solid ${tokens.colors.borderSubtle}`,
                            backgroundColor: tokens.colors.surface,
                            columnGap: tokens.spacing.md,
                            rowGap: tokens.spacing.xs,
                        }}
                    >
                        {/* Left: Title + Subtitle — click to select widget for Add to Chat */}
                        <Box
                            style={{
                                flex: '1 1 auto',
                                minWidth: 0,
                                overflow: 'hidden',
                                ...(onAddToChat
                                    ? { cursor: 'pointer', userSelect: 'none' }
                                    : {}),
                            }}
                            onClick={
                                onAddToChat
                                    ? () => setIsSelected((v) => !v)
                                    : undefined
                            }
                        >
                            <Box
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'baseline',
                                    gap: `${tokens.spacing.xxs}px ${tokens.spacing.sm}px`,
                                }}
                            >
                                {title &&
                                    (typeof title === 'string' ? (
                                        <Text
                                            style={{
                                                ...tokens.typography.title,
                                                color: tokens.colors
                                                    .textPrimary,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {title}
                                        </Text>
                                    ) : (
                                        title
                                    ))}
                                {subtitle && (
                                    <Text
                                        style={{
                                            ...tokens.typography.subtitle,
                                            color: tokens.colors.textMuted,
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {subtitle}
                                    </Text>
                                )}
                            </Box>
                        </Box>

                        {/*
                         * Right: Controls. Wraps to a second line rather than
                         * overflowing the clipped card — below ~900px the
                         * search + filters + toolbar controls cannot share a
                         * row with the title, and clipping silently hides the
                         * column picker and share buttons.
                         */}
                        <Group
                            style={{
                                gap: tokens.spacing.xs,
                                flexShrink: 0,
                                flexWrap: 'wrap',
                                justifyContent: 'flex-end',
                            }}
                        >
                            {/* Search */}
                            {enableSearch && (
                                <TextInput
                                    value={search}
                                    onChange={(e) =>
                                        handleSearchChange(e.target.value)
                                    }
                                    placeholder={searchPlaceholder}
                                    size="xs"
                                    leftSection={<MagnifyingGlass size={14} />}
                                    rightSection={
                                        search ? (
                                            <ActionIcon
                                                size="xs"
                                                variant="subtle"
                                                onClick={() =>
                                                    handleSearchChange('')
                                                }
                                            >
                                                <X size={12} />
                                            </ActionIcon>
                                        ) : null
                                    }
                                    styles={{
                                        input: {
                                            height: tokens.dimensions
                                                .controlHeight,
                                            minHeight:
                                                tokens.dimensions.controlHeight,
                                            fontSize: 11,
                                            border: `1px solid ${tokens.colors.border}`,
                                            borderRadius: 6,
                                            width: 160,
                                        },
                                        section: {
                                            width: 28,
                                        },
                                    }}
                                />
                            )}

                            {/* Entity filters */}
                            {filters?.map((f, i) => (
                                <EntityFilterDropdown
                                    key={i}
                                    label={f.label}
                                    params={f.params}
                                    value={f.value}
                                    onChange={f.onChange}
                                />
                            ))}

                            {/* Custom toolbar controls (date, sort, etc.) */}
                            {toolbarControls}

                            {/* Group By */}
                            {groupByOptions && groupByOptions.length > 0 && (
                                <Menu
                                    shadow="md"
                                    width={160}
                                    position="bottom-end"
                                >
                                    <Menu.Target>
                                        {activeGroupBy ? (
                                            <button
                                                type="button"
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    height: tokens.dimensions
                                                        .controlHeight,
                                                    padding: '0 8px',
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    color: tokens.colors
                                                        .activeTab,
                                                    background:
                                                        'rgba(99,102,241,0.06)',
                                                    border: `1px solid rgba(99,102,241,0.3)`,
                                                    borderRadius: 6,
                                                    cursor: 'pointer',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <Rows size={12} />
                                                {groupByOptions.find(
                                                    (o) =>
                                                        o.value ===
                                                        activeGroupBy,
                                                )?.label ?? activeGroupBy}
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onGroupByChange?.(null);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.stopPropagation();
                                                            onGroupByChange?.(
                                                                null,
                                                            );
                                                        }
                                                    }}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        marginLeft: 2,
                                                        opacity: 0.7,
                                                    }}
                                                >
                                                    <X size={10} />
                                                </span>
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    height: tokens.dimensions
                                                        .controlHeight,
                                                    padding: '0 8px',
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    color: tokens.colors
                                                        .textSecondary,
                                                    background: 'transparent',
                                                    border: `1px solid ${tokens.colors.border}`,
                                                    borderRadius: 6,
                                                    cursor: 'pointer',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <Rows size={12} />
                                                Group by
                                            </button>
                                        )}
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        {groupByOptions.map((opt) => (
                                            <Menu.Item
                                                key={opt.value}
                                                onClick={() =>
                                                    onGroupByChange?.(opt.value)
                                                }
                                                style={{ fontSize: 12 }}
                                            >
                                                {opt.label}
                                            </Menu.Item>
                                        ))}
                                    </Menu.Dropdown>
                                </Menu>
                            )}

                            {/* Show / hide columns */}
                            {enableColumnPicker && contentType === 'table' && columnPickerDefs.length > 1 && (
                                <ColumnPicker defaultOnDefs={columnPickerDefs} defaultOffDefs={[]} hiddenKeys={hiddenColumnKeys} onChange={setHiddenColumnKeys} />
                            )}

                            {/* Custom right-side icon actions (e.g. column visibility toggle) */}
                            {toolbarActionsRight}

                            {/* AI table-view agent — real tables only (not charts/custom/empty) */}
                            {enableAiView &&
                                contentType === 'table' &&
                                columnsProp.length > 0 && (
                                    <Group
                                        style={{
                                            gap: tokens.spacing.xxs,
                                            flexWrap: 'nowrap',
                                        }}
                                    >
                                        {aiSummary && (
                                            <Group
                                                style={{
                                                    gap: 4,
                                                    flexWrap: 'nowrap',
                                                    height: tokens.dimensions
                                                        .controlHeight,
                                                    padding: '0 8px',
                                                    borderRadius: 6,
                                                    maxWidth: 280,
                                                    backgroundColor:
                                                        'rgba(1,149,255,0.10)',
                                                    border: '1px solid rgba(1,149,255,0.25)',
                                                }}
                                            >
                                                <Sparkle
                                                    size={12}
                                                    weight="fill"
                                                    color="rgb(1,149,255)"
                                                />
                                                <Text
                                                    style={{
                                                        fontSize: 11,
                                                        color: 'rgb(1,120,210)',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                    }}
                                                    title={aiSummary}
                                                >
                                                    {aiSummary}
                                                </Text>
                                                <ActionIcon
                                                    variant="transparent"
                                                    size="xs"
                                                    onClick={resetAiView}
                                                    title="Reset to original view"
                                                >
                                                    <X
                                                        size={11}
                                                        color="rgb(1,120,210)"
                                                    />
                                                </ActionIcon>
                                            </Group>
                                        )}
                                        <Popover
                                            opened={aiOpen}
                                            onClose={() => setAiOpen(false)}
                                            position="bottom-end"
                                            withinPortal
                                            shadow="md"
                                            radius="md"
                                        >
                                            <Popover.Target>
                                                <Tooltip
                                                    label="Ask this table"
                                                    withArrow
                                                >
                                                    <ActionIcon
                                                        variant={
                                                            aiSummary
                                                                ? 'light'
                                                                : 'subtle'
                                                        }
                                                        color={
                                                            aiSummary
                                                                ? 'blue'
                                                                : 'gray'
                                                        }
                                                        size="sm"
                                                        onClick={() =>
                                                            setAiOpen((o) => !o)
                                                        }
                                                        style={{
                                                            height: tokens
                                                                .dimensions
                                                                .controlHeight,
                                                            width: tokens
                                                                .dimensions
                                                                .controlHeight,
                                                        }}
                                                    >
                                                        <Sparkle
                                                            size={16}
                                                            weight={
                                                                aiSummary
                                                                    ? 'fill'
                                                                    : 'regular'
                                                            }
                                                        />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Popover.Target>
                                            <Popover.Dropdown p={10}>
                                                <div style={{ width: 320 }}>
                                                    <Text
                                                        style={{
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            color: tokens.colors
                                                                .textSecondary,
                                                            marginBottom: 6,
                                                        }}
                                                    >
                                                        Ask this table
                                                    </Text>
                                                    <TextInput
                                                        placeholder="e.g. highlight rows where forecast > stock"
                                                        value={aiPrompt}
                                                        onChange={(e) =>
                                                            setAiPrompt(
                                                                e.currentTarget
                                                                    .value,
                                                            )
                                                        }
                                                        onKeyDown={(e) => {
                                                            if (
                                                                e.key ===
                                                                    'Enter' &&
                                                                !aiLoading
                                                            )
                                                                runAiView();
                                                        }}
                                                        size="xs"
                                                        autoFocus
                                                    />
                                                    {aiError && (
                                                        <Text
                                                            style={{
                                                                fontSize: 11,
                                                                color: 'rgb(220,38,38)',
                                                                marginTop: 6,
                                                            }}
                                                        >
                                                            {aiError}
                                                        </Text>
                                                    )}
                                                    <Group
                                                        style={{
                                                            justifyContent:
                                                                'flex-end',
                                                            gap: 6,
                                                            marginTop: 8,
                                                        }}
                                                    >
                                                        {aiSummary && (
                                                            <button
                                                                type="button"
                                                                onClick={
                                                                    resetAiView
                                                                }
                                                                style={{
                                                                    fontSize: 11,
                                                                    color: tokens
                                                                        .colors
                                                                        .textSecondary,
                                                                    background:
                                                                        'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                Reset
                                                            </button>
                                                        )}
                                                        <ActionIcon
                                                            variant="filled"
                                                            color="blue"
                                                            size="md"
                                                            loading={aiLoading}
                                                            onClick={runAiView}
                                                            disabled={
                                                                !aiPrompt.trim()
                                                            }
                                                            title="Apply"
                                                        >
                                                            <Sparkle
                                                                size={14}
                                                                weight="fill"
                                                            />
                                                        </ActionIcon>
                                                    </Group>
                                                </div>
                                            </Popover.Dropdown>
                                        </Popover>
                                    </Group>
                                )}

                            {/* Email */}
                            {emailControl}

                            {/* Download */}
                            {enableDownload && (
                                <Tooltip
                                    label={
                                        isDownloading
                                            ? 'Preparing…'
                                            : 'Download CSV'
                                    }
                                    withArrow
                                >
                                    <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        size="sm"
                                        aria-label="Download CSV"
                                        loading={isDownloading}
                                        onClick={handleBuiltinDownload}
                                        style={{
                                            height: tokens.dimensions
                                                .controlHeight,
                                            width: tokens.dimensions
                                                .controlHeight,
                                        }}
                                    >
                                        <DownloadSimple size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            )}

                            {/* Share */}
                            {onShare && (
                                <Tooltip label="Share" withArrow>
                                    <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        size="sm"
                                        onClick={() => onShare(activeTab)}
                                        style={{
                                            height: tokens.dimensions
                                                .controlHeight,
                                            width: tokens.dimensions
                                                .controlHeight,
                                        }}
                                    >
                                        <ShareNetwork size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Group>
                    </Box>
                )}

                {/* Data Grid - Either internal table or children */}
                {useInternalTable ? (
                    <div
                        ref={tableScrollRef}
                        style={{
                            flex: 1,
                            minHeight: 0,
                            maxHeight:
                                maxHeight === 'auto'
                                    ? undefined
                                    : maxHeight || undefined,
                            overflowY: 'auto',
                            overflowX: 'auto',
                            // See the bare-mode wrapper: the virtualizer owns
                            // scroll correction, so browser anchoring stays off.
                            overflowAnchor: 'none',
                            // No-op on macOS (overlay scrollbars); on Windows it
                            // stops the grid jumping sideways when the vertical
                            // scrollbar appears or disappears mid-filter.
                            scrollbarGutter: 'stable',
                        }}
                    >
                        <table style={tableStyle}>
                            {renderColGroup()}
                            {/* Column Group Headers (if any) */}
                            {hasGroupHeaders &&
                                (() => {
                                    // Calculate sticky column info for group headers
                                    const stickyColumns = columns.filter(
                                        (c) => c.sticky,
                                    );
                                    const totalStickyWidth =
                                        stickyColumns.reduce(
                                            (sum, col) =>
                                                sum + resolveColumnWidth(col),
                                            0,
                                        );
                                    const stickyColumnCount =
                                        stickyColumns.length;

                                    // Track column index as we iterate through groups
                                    let columnIndex = 0;

                                    return (
                                        <thead
                                            style={{
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 20,
                                            }}
                                        >
                                            {renderSuperGroupRow()}
<tr
                                                style={{
                                                    backgroundColor:
                                                        tokens.colors.headerBg,
                                                }}
                                            >
                                                {columnGroups.map(
                                                    (group, gIdx) => {
                                                        const groupStartIndex =
                                                            columnIndex;
                                                        const groupEndIndex =
                                                            columnIndex +
                                                            group.columns
                                                                .length -
                                                            1;
                                                        columnIndex +=
                                                            group.columns
                                                                .length;

                                                        // Check if this group contains any sticky columns
                                                        const containsStickyColumns =
                                                            groupStartIndex <
                                                            stickyColumnCount;
                                                        // Check if this group contains ONLY sticky columns
                                                        const isFullySticky =
                                                            groupEndIndex <
                                                            stickyColumnCount;
                                                        // Check if this is the last group that contains sticky columns
                                                        const isLastStickyGroup =
                                                            containsStickyColumns &&
                                                            groupEndIndex >=
                                                                stickyColumnCount -
                                                                    1;

                                                        // Calculate left offset for sticky group headers
                                                        let leftOffset = 0;
                                                        if (
                                                            containsStickyColumns &&
                                                            isFullySticky
                                                        ) {
                                                            for (
                                                                let i = 0;
                                                                i <
                                                                groupStartIndex;
                                                                i++
                                                            ) {
                                                                const col =
                                                                    columns[i];
                                                                if (
                                                                    col?.sticky
                                                                ) {
                                                                    leftOffset +=
                                                                        resolveColumnWidth(
                                                                            col,
                                                                        );
                                                                }
                                                            }
                                                        }

                                                        return (
                                                            <th
                                                                key={gIdx}
                                                                colSpan={
                                                                    group
                                                                        .columns
                                                                        .length
                                                                }
                                                                style={{
                                                                    padding: `${tokens.spacing.xs}px ${tokens.spacing.md}px`,
                                                                    borderBottom: `1px solid ${tokens.colors.border}`,
                                                                    borderRight:
                                                                        gIdx <
                                                                        columnGroups.length -
                                                                            1
                                                                            ? `1px solid ${tokens.colors.borderStrong}`
                                                                            : 'none',
                                                                    textAlign:
                                                                        'center',
                                                                    ...tokens
                                                                        .typography
                                                                        .header,
                                                                    color: tokens
                                                                        .colors
                                                                        .textSecondary,
                                                                    // Make sticky if this group contains only sticky columns
                                                                    position:
                                                                        isFullySticky
                                                                            ? 'sticky'
                                                                            : 'relative',
                                                                    left: isFullySticky
                                                                        ? leftOffset
                                                                        : undefined,
                                                                    zIndex: isFullySticky
                                                                        ? 20
                                                                        : 1,
                                                                    // Use opaque background for sticky groups
                                                                    backgroundColor:
                                                                        isFullySticky
                                                                            ? tokens
                                                                                  .colors
                                                                                  .headerBgSticky
                                                                            : tokens
                                                                                  .colors
                                                                                  .headerBg,
                                                                    boxShadow:
                                                                        isLastStickyGroup &&
                                                                        isFullySticky
                                                                            ? '4px 0 8px rgba(0,0,0,0.08)'
                                                                            : undefined,
                                                                }}
                                                            >
                                                                {group.name ||
                                                                    ''}
                                                            </th>
                                                        );
                                                    },
                                                )}
                                            </tr>
                                        </thead>
                                    );
                                })()}

                            {/* Column Headers */}
                            <thead
                                style={{
                                    position: 'sticky',
                                    top: groupBandHeight,
                                    zIndex: 20,
                                    backgroundColor: tokens.colors.headerBg,
                                }}
                            >
                                <tr
                                    style={{
                                        backgroundColor: tokens.colors.headerBg,
                                    }}
                                >
                                    {columns.map((col, idx) => {
                                        // Calculate left position for sticky columns
                                        let leftOffset = 0;
                                        if (col.sticky) {
                                            for (let i = 0; i < idx; i++) {
                                                if (columns[i].sticky) {
                                                    leftOffset +=
                                                        resolveColumnWidth(
                                                            columns[i],
                                                        );
                                                }
                                            }
                                        }
                                        // Check if this is the last sticky column (to add shadow)
                                        const isLastSticky =
                                            col.sticky &&
                                            (idx === columns.length - 1 ||
                                                !columns[idx + 1]?.sticky);

                                        const stickyBg = col.sticky
                                            ? tokens.colors.headerBgSticky
                                            : tokens.colors.headerBg;
                                        return (
                                            <th
                                                key={col.key}
                                                onClick={
                                                    col.sortable
                                                        ? () =>
                                                              handleSort(
                                                                  col.key,
                                                              )
                                                        : undefined
                                                }
                                                style={{
                                                    padding: `${tokens.spacing.sm}px ${tokens.spacing.md}px`,
                                                    borderBottom: `1px solid ${tokens.colors.border}`,
                                                    borderRight:
                                                        groupBoundaryIndices.has(
                                                            idx,
                                                        )
                                                            ? `1px solid ${tokens.colors.borderStrong}`
                                                            : idx <
                                                                columns.length -
                                                                    1
                                                              ? `1px solid ${tokens.colors.borderSubtle}`
                                                              : 'none',
                                                    textAlign:
                                                        col.align || 'left',
                                                    width: resolveColumnWidth(
                                                        col,
                                                    ),
                                                    minWidth:
                                                        resolveColumnWidth(col),
                                                    position: col.sticky
                                                        ? 'sticky'
                                                        : 'relative',
                                                    left: col.sticky
                                                        ? leftOffset
                                                        : undefined,
                                                    zIndex: col.sticky ? 20 : 1,
                                                    // Use opaque background for sticky columns to prevent text bleed-through
                                                    backgroundColor: stickyBg,
                                                    cursor: col.sortable
                                                        ? 'pointer'
                                                        : 'default',
                                                    userSelect: 'none',
                                                    boxShadow: isLastSticky
                                                        ? '4px 0 8px rgba(0,0,0,0.08)'
                                                        : undefined,
                                                    ...tokens.typography.header,
                                                    color: tokens.colors
                                                        .textSecondary,
                                                    transition:
                                                        'background-color 0.15s ease',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor =
                                                        col.sticky
                                                            ? '#EEEEEE'
                                                            : 'rgba(0,0,0,0.04)';
                                                    setHoveredHeaderKey(
                                                        col.key,
                                                    );
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor =
                                                        stickyBg;
                                                    setHoveredHeaderKey(null);
                                                }}
                                            >
                                                <Group
                                                    style={{
                                                        gap: tokens.spacing.xxs,
                                                        justifyContent:
                                                            col.align ===
                                                            'right'
                                                                ? 'flex-end'
                                                                : col.align ===
                                                                    'center'
                                                                  ? 'center'
                                                                  : 'flex-start',
                                                        flexWrap: 'nowrap',
                                                        // Backstop: if a header
                                                        // word still
                                                        // out-measures the
                                                        // column, clip it
                                                        // rather than bleed
                                                        // over the neighbouring
                                                        // opaque header. A
                                                        // control column (empty
                                                        // header) has no label
                                                        // to bleed, and its
                                                        // checkbox must stay
                                                        // whole.
                                                        overflow:
                                                            col.header === ''
                                                                ? 'visible'
                                                                : 'hidden',
                                                    }}
                                                >
                                                    {renderHeaderCellContent(
                                                        col,
                                                    )}
                                                </Group>
                                                {renderColumnFilter(col)}
                                            </th>
                                        );
                                    })}
                                </tr>
                                {hasAggregateRow && renderAggregateRow()}
                            </thead>

                            {/* Body */}
                            <tbody>
                                {/* Loading State */}
                                {isLoading && (
                                    <>
                                        {Array.from({ length: 5 }).map(
                                            (_, i) => (
                                                <SkeletonRow
                                                    key={i}
                                                    columns={columns.length}
                                                />
                                            ),
                                        )}
                                    </>
                                )}

                                {/* Empty State */}
                                {showEmptyState && (
                                    <tr>
                                        <td
                                            colSpan={columns.length}
                                            style={{
                                                padding: `${tokens.spacing.lg * 2}px ${tokens.spacing.md}px`,
                                                textAlign: 'center',
                                                color: tokens.colors.textMuted,
                                                fontSize: 13,
                                            }}
                                        >
                                            {emptyState || 'No data available'}
                                        </td>
                                    </tr>
                                )}

                                {/* Data Rows — virtualized */}
                                {!isLoading && hasData && (
                                    <>
                                        {paddingTop > 0 && (
                                            <tr aria-hidden="true">
                                                <td
                                                    colSpan={columns.length}
                                                    style={{
                                                        height: paddingTop,
                                                        padding: 0,
                                                        border: 'none',
                                                    }}
                                                />
                                            </tr>
                                        )}
                                        {virtualItems.map((virtualRow) => {
                                            const flat =
                                                flatRows[virtualRow.index];
                                            const row = flat.row;
                                            const rowKey = flat.key;
                                            const rowClassName =
                                                getRowClassName?.(
                                                    row,
                                                    virtualRow.index,
                                                );
                                            const highlightBg =
                                                highlightByKey.get(rowKey)
                                                    ? HIGHLIGHT_BG[
                                                          highlightByKey.get(
                                                              rowKey,
                                                          )!
                                                      ]
                                                    : undefined;
                                            return (
                                                <tr
                                                    key={rowKey}
                                                    ref={
                                                        rowVirtualizer.measureElement
                                                    }
                                                    data-index={
                                                        virtualRow.index
                                                    }
                                                    className={rowClassName}
                                                    onClick={
                                                        onRowClick ||
                                                        flat.hasSubRows
                                                            ? () =>
                                                                  handleRowClick(
                                                                      flat,
                                                                      virtualRow.index,
                                                                  )
                                                            : undefined
                                                    }
                                                    style={{
                                                        height: estimatedRowHeight,
                                                        cursor:
                                                            onRowClick ||
                                                            flat.hasSubRows
                                                                ? 'pointer'
                                                                : 'default',
                                                        backgroundColor:
                                                            highlightBg,
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor =
                                                            tokens.colors.hoverRow;
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor =
                                                            highlightBg ?? '';
                                                    }}
                                                >
                                                    {columns.map(
                                                        (col, colIdx) => {
                                                            const value =
                                                                row[col.key];
                                                            const rendered =
                                                                col.render
                                                                    ? col.render(
                                                                          value,
                                                                          row,
                                                                          virtualRow.index,
                                                                      )
                                                                    : (value?.toString() ??
                                                                      '—');
                                                            const content =
                                                                colIdx === 0
                                                                    ? renderWithExpandAffordance(
                                                                          <>
                                                                              {rendered}
                                                                              {/* Says a number moved, and what moved into it. */}
                                                                              {explosion?.enabled && (
                                                                                  <ExplodedBadge
                                                                                      contributors={
                                                                                          row[
                                                                                              EXPLOSION_ORIGIN_KEY
                                                                                          ]
                                                                                              ?.contributors ??
                                                                                          []
                                                                                      }
                                                                                      onClick={
                                                                                          explosion.onShowDetail
                                                                                              ? () =>
                                                                                                    explosion.onShowDetail?.(
                                                                                                        row,
                                                                                                    )
                                                                                              : undefined
                                                                                      }
                                                                                  />
                                                                              )}
                                                                          </>,
                                                                          flat,
                                                                      )
                                                                    : rendered;
                                                            let leftOffset = 0;
                                                            if (col.sticky) {
                                                                for (
                                                                    let i = 0;
                                                                    i < colIdx;
                                                                    i++
                                                                ) {
                                                                    if (
                                                                        columns[
                                                                            i
                                                                        ].sticky
                                                                    )
                                                                        leftOffset +=
                                                                            resolveColumnWidth(
                                                                                columns[
                                                                                    i
                                                                                ],
                                                                            );
                                                                }
                                                            }
                                                            const isLastSticky =
                                                                col.sticky &&
                                                                (colIdx ===
                                                                    columns.length -
                                                                        1 ||
                                                                    !columns[
                                                                        colIdx +
                                                                            1
                                                                    ]?.sticky);
                                                            return (
                                                                <td
                                                                    key={
                                                                        col.key
                                                                    }
                                                                    className={bodyCellClassName(col, colIdx === 0 ? rowClassName : undefined)}
                                                    onMouseEnter={revealClippedCellText}
                                                                    style={{
                                                                        padding: `${tokens.spacing.sm}px ${tokens.spacing.md}px`,
                                                                        borderBottom: `1px solid ${tokens.colors.borderSubtle}`,
                                                                        borderRight:
                                                                            colIdx <
                                                                            columns.length -
                                                                                1
                                                                                ? `1px solid ${tokens.colors.borderSubtle}`
                                                                                : 'none',
                                                                        textAlign:
                                                                            col.align ||
                                                                            'left',
                                                                        height: estimatedRowHeight,
                                                                        verticalAlign:
                                                                            'middle',
                                                                        position:
                                                                            col.sticky
                                                                                ? 'sticky'
                                                                                : 'relative',
                                                                        left: col.sticky
                                                                            ? leftOffset
                                                                            : undefined,
                                                                        zIndex: col.sticky
                                                                            ? 10
                                                                            : 1,
                                                                        backgroundColor:
                                                                            col.sticky
                                                                                ? tokens
                                                                                      .colors
                                                                                      .surfaceSticky
                                                                                : 'inherit',
                                                                        boxShadow:
                                                                            isLastSticky
                                                                                ? '4px 0 8px rgba(0,0,0,0.08)'
                                                                                : undefined,
                                                                        ...tokens
                                                                            .typography
                                                                            .cell,
                                                                        color: tokens
                                                                            .colors
                                                                            .textPrimary,
                                                                    }}
                                                                >
                                                                    {content}
                                                                </td>
                                                            );
                                                        },
                                                    )}
                                                </tr>
                                            );
                                        })}
                                        {paddingBottom > 0 && (
                                            <tr aria-hidden="true">
                                                <td
                                                    colSpan={columns.length}
                                                    style={{
                                                        height: paddingBottom,
                                                        padding: 0,
                                                        border: 'none',
                                                    }}
                                                />
                                            </tr>
                                        )}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : children ? (
                    // Custom children content (charts, custom tables, etc.)
                    <Box
                        style={{
                            flex: 1,
                            minHeight:
                                minHeight ||
                                (contentType === 'chart' ? 300 : 0),
                            maxHeight:
                                maxHeight === 'auto'
                                    ? undefined
                                    : maxHeight || undefined,
                            overflowY: 'auto',
                        }}
                    >
                        {children}
                    </Box>
                ) : null}

                {/*
                 * Bottom bar — legend, built-in pagination and custom footer
                 * share a single row. They used to be three stacked bars at
                 * ~45px each; on a short viewport that is three rows of data
                 * spent on chrome. `flex-wrap` lets them fall back to separate
                 * lines when the grid is genuinely too narrow to share.
                 */}
                {(legend || activePaginationOptions || footer) && (
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            columnGap: tokens.spacing.md,
                            rowGap: tokens.spacing.xs,
                            padding: `${tokens.spacing.xs}px ${tokens.spacing.md}px`,
                            borderTop: `1px solid ${tokens.colors.border}`,
                            backgroundColor: tokens.colors.surface,
                        }}
                    >
                        {legend && (
                            <Box style={{ flex: '0 1 auto', minWidth: 0 }}>
                                {legend}
                            </Box>
                        )}

                        {/* Built-in pagination — shown when pagination options are active */}
                        {activePaginationOptions && (
                            <Box style={{ flex: '1 1 340px', minWidth: 0 }}>
                                <DataSheetPagination
                                    currentPage={page}
                                    totalPages={totalPages}
                                    pageSize={pageSize}
                                    totalItems={displayData.length}
                                    pageSizeOptions={activePaginationOptions}
                                    onPageChange={setPage}
                                    onPageSizeChange={(size) => {
                                        setPageSize(size);
                                        setPage(1);
                                    }}
                                />
                            </Box>
                        )}

                        {footer && (
                            <Box style={{ flex: '1 1 340px', minWidth: 0 }}>
                                {footer}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
            {emailOpen && (
                <Suspense fallback={null}>
                    <EmailComposerDrawer
                        opened
                        onClose={() => setEmailOpen(false)}
                        defaultName={emailDefaultName}
                        currentUserEmail={currentUserEmail}
                        currentFilters={{}}
                        captureImage={(maxVisualRows) =>
                            captureOrFail((handle) => handle.captureViewImage({ maxVisualRows }))
                        }
                        captureData={(maxVisualRows) =>
                            captureOrFail((handle) => handle.captureViewData({ maxVisualRows }))
                        }
                        pageCapture={captureKey ? { path: currentAppPath(), sheetKey: captureKey } : undefined}
                    />
                </Suspense>
            )}
        </div>
    );
}

// Forward ref with generic support
// NOTE: `as unknown as` avoids Babel TSX ambiguity with `as <T extends ...>` generic casts
type DataSheetComponent = <T extends Record<string, any>>(
    props: DataSheetProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;
export const DataSheet = React.forwardRef(
    DataSheetInner,
) as unknown as DataSheetComponent;

// =============================================================================
// HELPER COMPONENTS FOR COMMON PATTERNS
// =============================================================================

/** Pagination Footer */
export interface DataSheetPaginationProps {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalItems: number;
    pageSizeOptions?: number[];
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
}

export const DataSheetPagination = memo<DataSheetPaginationProps>(
    ({
        currentPage,
        totalPages,
        pageSize,
        totalItems,
        pageSizeOptions = [20, 50, 100],
        onPageChange,
        onPageSizeChange,
    }) => (
        <Group
            justify="space-between"
            style={{ fontSize: 12, color: tokens.colors.textSecondary }}
        >
            <Group style={{ gap: tokens.spacing.xs }}>
                <Text size="xs">
                    Page <strong>{currentPage}</strong> of{' '}
                    <strong>{totalPages || 1}</strong>
                </Text>
                <Text size="xs" c="dimmed">
                    • {totalItems} items
                </Text>
            </Group>
            <Group style={{ gap: tokens.spacing.xs }}>
                {onPageSizeChange && (
                    <select
                        value={pageSize}
                        onChange={(e) =>
                            onPageSizeChange(Number(e.target.value))
                        }
                        style={{
                            padding: '4px 8px',
                            fontSize: 11,
                            border: `1px solid ${tokens.colors.border}`,
                            borderRadius: 4,
                            backgroundColor: tokens.colors.surface,
                            height: tokens.dimensions.controlHeight,
                        }}
                    >
                        {pageSizeOptions.map((size) => (
                            <option key={size} value={size}>
                                Show {size}
                            </option>
                        ))}
                    </select>
                )}
                <Group style={{ gap: 2 }}>
                    <button
                        type="button"
                        onClick={() => onPageChange(1)}
                        disabled={currentPage <= 1}
                        style={{
                            padding: '4px 8px',
                            fontSize: 11,
                            border: `1px solid ${tokens.colors.border}`,
                            borderRadius: 4,
                            backgroundColor: tokens.colors.surface,
                            cursor:
                                currentPage <= 1 ? 'not-allowed' : 'pointer',
                            opacity: currentPage <= 1 ? 0.4 : 1,
                            height: tokens.dimensions.controlHeight,
                        }}
                    >
                        «
                    </button>
                    <button
                        type="button"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage <= 1}
                        style={{
                            padding: '4px 8px',
                            fontSize: 11,
                            border: `1px solid ${tokens.colors.border}`,
                            borderRadius: 4,
                            backgroundColor: tokens.colors.surface,
                            cursor:
                                currentPage <= 1 ? 'not-allowed' : 'pointer',
                            opacity: currentPage <= 1 ? 0.4 : 1,
                            height: tokens.dimensions.controlHeight,
                        }}
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        style={{
                            padding: '4px 8px',
                            fontSize: 11,
                            border: `1px solid ${tokens.colors.border}`,
                            borderRadius: 4,
                            backgroundColor: tokens.colors.surface,
                            cursor:
                                currentPage >= totalPages
                                    ? 'not-allowed'
                                    : 'pointer',
                            opacity: currentPage >= totalPages ? 0.4 : 1,
                            height: tokens.dimensions.controlHeight,
                        }}
                    >
                        ›
                    </button>
                    <button
                        type="button"
                        onClick={() => onPageChange(totalPages)}
                        disabled={currentPage >= totalPages}
                        style={{
                            padding: '4px 8px',
                            fontSize: 11,
                            border: `1px solid ${tokens.colors.border}`,
                            borderRadius: 4,
                            backgroundColor: tokens.colors.surface,
                            cursor:
                                currentPage >= totalPages
                                    ? 'not-allowed'
                                    : 'pointer',
                            opacity: currentPage >= totalPages ? 0.4 : 1,
                            height: tokens.dimensions.controlHeight,
                        }}
                    >
                        »
                    </button>
                </Group>
            </Group>
        </Group>
    ),
);
DataSheetPagination.displayName = 'DataSheetPagination';

/** Status Badge */
export interface DataSheetStatusBadgeProps {
    status: 'critical' | 'warning' | 'positive' | 'info' | 'neutral';
    label: string;
}

const statusColors = {
    critical: { bg: 'rgba(205, 101, 101, 0.1)', text: 'rgb(205, 101, 101)' },
    warning: { bg: 'rgba(219, 182, 88, 0.1)', text: 'rgb(175, 146, 70)' },
    positive: { bg: 'rgba(45, 187, 136, 0.1)', text: 'rgb(35, 150, 110)' },
    info: { bg: 'rgba(1, 149, 255, 0.1)', text: 'rgb(1, 119, 204)' },
    neutral: { bg: 'rgba(0, 0, 0, 0.04)', text: 'rgb(100, 116, 139)' },
};

export const DataSheetStatusBadge = memo<DataSheetStatusBadgeProps>(
    ({ status, label }) => (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 8px',
                fontSize: 11,
                fontWeight: 500,
                borderRadius: 4,
                backgroundColor: statusColors[status].bg,
                color: statusColors[status].text,
            }}
        >
            {label}
        </span>
    ),
);
DataSheetStatusBadge.displayName = 'DataSheetStatusBadge';

/** Numeric Cell (right-aligned, tabular nums) */
export const DataSheetNumericCell = memo<{
    value: number | null | undefined;
    decimals?: number;
}>(({ value, decimals = 0 }) => (
    <span
        style={{
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 500,
        }}
    >
        {value !== null && value !== undefined ? formatCount(value, { decimals }) : '—'}
    </span>
));
DataSheetNumericCell.displayName = 'DataSheetNumericCell';

/** Legend Component */
export interface DataSheetLegendItem {
    color: string;
    label: string;
}

export interface DataSheetLegendProps {
    title?: string;
    items: DataSheetLegendItem[];
}

export const DataSheetLegend = memo<DataSheetLegendProps>(
    ({ title, items }) => (
        <Group
            style={{
                gap: tokens.spacing.md,
                fontSize: 12,
                color: tokens.colors.textSecondary,
            }}
        >
            {title && (
                <Text
                    style={{
                        fontWeight: 500,
                        color: tokens.colors.textPrimary,
                        fontSize: 12,
                    }}
                >
                    {title}
                </Text>
            )}
            {items.map((item, idx) => (
                <Group key={idx} style={{ gap: 6 }}>
                    <span
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: item.color,
                            flexShrink: 0,
                        }}
                    />
                    <span style={{ fontSize: 12 }}>{item.label}</span>
                </Group>
            ))}
        </Group>
    ),
);
DataSheetLegend.displayName = 'DataSheetLegend';

export default DataSheet;
